import React, { useEffect, useRef, useState, useMemo, useCallback, useId } from 'react';
import autosize from 'autosize';
import dynamic from 'next/dynamic';
import { LocaleDetails } from 'shared/src/types/languages.type';
import type { ShelfSubregionScope } from '@/lib/shelf-region-scope';
import { CategoryMap } from 'shared/src/types/categories.type';
import { Message } from 'ai/react';
import { getSearchUiContent } from '@/lib/ui-content/search-ui-content';
import { retryLabel, useRetryCountdown } from './use-retry-countdown';
import { useStreamStartTimeout } from './stream-start-timeout';
import { SearchBarPills } from '../search-bar-pills';
import { DiscussionThread } from './chat/discussion-thread';
import type { UserLocation } from '@/lib/user-location';
import { ButtonSuggestion } from './chat/message';
import { PromptBanner } from './prompt-banner';
import { AboveBarPromptPanel } from './above-bar-prompt-panel';
import {
  budgetCurrencyAdornment,
  fillBudgetCopy,
  formatBudgetLabel,
  getBudgetUiContent,
} from '@/lib/ui-content/budget-ui-content';
import { replacePlaceholders } from '@/lib/ui-content/price-ui-content';
import { parseSuggestionsMessage, parseAssistantMessage, unwrapUserInput } from '@/lib/parse-assistant-message';
import { bannerCopyForTurn } from '@/lib/search-banner-copy';
import { AboveBarHistory, type HistoryTurn } from './above-bar-history';
import { buildKitTrayEntries } from '@/lib/kit-tray-entries';
import { dismissChatTranscriptResume, getChatTranscript, getChatTranscripts } from '@/lib/chat-history';
import { ResumeHistoryChip } from './resume-history-chip';
import { queryBelongsToTranscript, readSearchReturnStash, SEARCH_RETURN_WINDOW_MS } from '@/lib/search-return-stash';
import { AlgoliaHit, SearchIntent } from 'shared/src/types/search.type';
import { useDebounce } from 'use-debounce';
import { MIN_SUBCATEGORIES_FOR_LIST } from 'shared/src/lib/models';
import { ChatRequestOptions } from 'ai';
import { MdClear, MdStop } from 'react-icons/md';
import { HiClock as ClockIcon, HiPlus as PlusIcon } from 'react-icons/hi';
import type { VoiceSearchControl } from './speech-to-text-button';
import { AttachmentChipsRail } from './attachment-chips';
import type { AddAttachmentResult, ComposerAttachment } from './composer-attachments';
import { classifyPaste, replaceLinksInText, scanLinks } from './paste-attachments';
import { snapSelection, repairDamagedTokens, tokenArrow } from './link-token-editing';
import {
  findLinkTokens,
  insertInlineText,
  linkTokenLabel,
  linkTokenText,
  removeLinkToken,
  stripLinkTokens,
} from 'shared/src/utils/link-tokens';
import { attachmentSummaryLabel, readMessageAttachments } from './composer-attachments';
import { shouldDismissKeyboardForPanel, shouldHoldComposerOpen } from './composer-engagement';
import {
  CONVERSATION_RETURN_MIN_PX,
  FOREIGN_SCROLL_MIN_PX,
  GESTURE_SCROLL_PX,
  KEYBOARD_CLOSED_HOLD_MS,
  KEYBOARD_LIFT_EASING,
  KEYBOARD_LIFT_MS,
  KEYBOARD_OPEN_THRESHOLD_PX,
  KEYBOARD_SETTLE_MS,
  LIFT_CHECK_AFTER_MS,
  LIFT_CHECK_MIN_PX,
  conversationReturnScrollTop,
  isKeyboardLeaving,
  isKeyboardOpen,
  keyboardCompositionTop,
  keyboardInsetPx,
  keyboardPlacementDelta,
  shelfScrollTarget,
  shouldAttemptRecenter,
  shouldLowerComposer,
} from './search-bar-viewport';
import { glideWindowScrollTo } from './glide-window-scroll';
import { readSettledLayout } from './settled-layout';
import { LoadingStatusRow } from './loading-status-row';
import { CardTopRegion } from './card-top-region';
import { heroSlotMax } from './hero-dock';
import { usePhoneThreadFit } from './phone-thread-fit';
import { focusVisualsFromHits } from '@/lib/focus-visuals';

/** Stable default so effects keyed on `attachments` do not re-run every render. */
const EMPTY_ATTACHMENTS: ComposerAttachment[] = [];

const SuggestionChips = dynamic(() => import('./suggestion-chips').then((m) => m.SuggestionChips), { ssr: false });
const LoadingExtras = dynamic(() => import('./loading-clean').then((m) => m.LoadingExtras), { ssr: false });
const LiveResultsShelf = dynamic(() => import('./live-results-shelf').then((m) => m.LiveResultsShelf), { ssr: false });
const ShelfEmptiedNote = dynamic(() => import('./shelf-emptied-note').then((m) => m.ShelfEmptiedNote), { ssr: false });
const Messages = dynamic(() => import('./chat/messages').then((m) => m.Messages), { ssr: false });
const CityRegionSelector = dynamic(() => import('../city-region-selector').then((m) => m.CityRegionSelector), { ssr: false });
const AttachMenu = dynamic(() => import('./attach-menu').then((m) => m.AttachMenu), { ssr: false });
const SearchHelpPanel = dynamic(
  () => import('./search-help/search-help-panel').then((m) => m.SearchHelpPanel),
  { ssr: false }
);
const VoiceSearchButton = dynamic(() => import('./speech-to-text-button').then((m) => m.VoiceSearchButton), { ssr: false });
const VoiceWaveAnimation = dynamic(() => import('./voice-wave-animation').then((m) => m.VoiceWaveAnimation), { ssr: false });

// Decorative 5-bar equalizer for the voice stop controls. Deliberately not
// mic-reactive (that's VoiceWaveAnimation's job in the input row) — it only
// keeps the big red "stop" affordance reading as live. Keyframes in globals.css.
const VoiceEqBars: React.FC = () => (
  <span aria-hidden className="flex h-5 flex-shrink-0 items-center gap-[3px]">
    {[0, 1, 2, 3, 4].map((i) => (
      <span
        key={i}
        className="h-full w-[3px] animate-voice-eq rounded-full bg-white/90"
        style={{ animationDelay: `${i * 130}ms` }}
      />
    ))}
  </span>
);
import { useUiContent } from '@/components/ui-content-provider';
import { isGiftEventPeriod } from '@/lib/special-events';
import { getSearchExamples } from '@/lib/ui-content/demo';
import { useRotatingPlaceholder } from '@/lib/hooks/use-rotating-placeholder';

const PreSearchSuggestionList = dynamic(() => import('./search-bar-suggestions').then((mod) => mod.PreSearchSuggestionList));

type HandleSearchPropositionSubmit = (event?: { preventDefault?: () => void }, chatRequestOptions?: ChatRequestOptions) => void;

/**
 * How recent a conversation must be for a browser Back onto the search page to
 * restore it outright. Long enough to cover "I opened the page, read it, came
 * back"; short enough that yesterday's search never reappears on its own.
 * Shared with the sessionStorage stash's own freshness window — see
 * lib/search-return-stash.ts.
 */
const RESUME_ON_BACK_WINDOW_MS = SEARCH_RETURN_WINDOW_MS;

// How long the "pick up where you left off" chip waits after the bar loses
// focus before it fades in — long enough that it doesn't feel like it's
// chasing the cursor away, short enough to still read as a reaction to it.
const RESUME_CHIP_DELAY_MS = 1000;

interface TextAreaSearchBarProps {
  inputRef: React.RefObject<HTMLTextAreaElement>;

  algoliaMatchs: AlgoliaHit[] | AlgoliaHit | undefined;
  /** The latest non-concluding turn's shelf lookup settled with nothing to show. */
  shelfSettledEmpty?: boolean;
  enterPressed: boolean;
  header?: boolean;
  /** In-flow header search: same chrome as `header`, not `position: fixed`. */
  embedded?: boolean;
  input: string;
  isDemo: boolean;
  isDoneFinal?: boolean;
  isLoading: boolean;
  isSearching: boolean;
  locale: LocaleDetails;
  messages: Message[];
  result?: { map: CategoryMap; bestProducts: string[]; locale: LocaleDetails; url: string };
  searchInput: string;
  /**
   * Pre-buy seeded conversation: the product being bought. Pinned in the thread
   * header, and — once the kit tray is up — as the tray's slot-0 anchor.
   */
  seedProduct?: { title: string; image?: string; categoryQuery?: string } | null;
  /** Bumped when the bar opens a conversation without the shopper focusing it (a pre-buy "Show me"). */
  revealConversationTick?: number;
  /**
   * Title of the product whose "Ask a question?" chip the shopper pressed, while it is
   * still the one they are on. The empty bar then reads "Ask about <product>…", focused
   * or not, so the question's destination and subject are both on screen.
   */
  askingAbout?: string;
  aiGeneratedQuery: string;
  /** Localized "browsing the web" status, non-empty only while the product resolver searches. */
  browsingLabel?: string;
  showNote: boolean;
  searchRejectedMessage?: string | null;
  setSearchRejectedMessage?: (msg: string | null) => void;
  topMenu?: boolean;

  searchPropositions: Message[];
  setSearchPropositions: React.Dispatch<React.SetStateAction<Message[]>>;
  handleSearchPropositionSubmit: HandleSearchPropositionSubmit;
  stopSearchProposition: () => void;
  setSearchPropositionInput: (input: string) => void;

  cancelSearch: () => void;
  /** The loading row's Stop: drops the page being built and returns to the moment before it. */
  stopSearch: () => void;

  setEnterPressed: React.Dispatch<React.SetStateAction<boolean>>;
  setFocusingOnTextArea: React.Dispatch<React.SetStateAction<boolean>>;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  setIsDemo: React.Dispatch<React.SetStateAction<boolean>>;
  setDemoType?: React.Dispatch<React.SetStateAction<'product' | 'category' | null>>;
  setIsSearching: React.Dispatch<React.SetStateAction<boolean>>;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setSearchInput: React.Dispatch<React.SetStateAction<string>>;
  setShowNote: React.Dispatch<React.SetStateAction<boolean>>;

  // Attachments: what waits in the bar to be sent (a photo, links, notes),
  // added from the "+" menu or by pasting.
  attachments?: ComposerAttachment[];
  onAddImage?: (file: File) => void;
  onAddLink?: (raw: string) => AddAttachmentResult;
  onAddNote?: (text: string) => AddAttachmentResult;
  onRemoveAttachment?: (id: string) => void;
  /** Send what is attached with no words (a pasted link on its own). */
  onSubmitAttachmentsOnly?: () => void;

  // Live results shelf (below the bar)
  /** User bypasses the matched pages and asks for a freshly created page. */
  onCreatePageFromShelf?: () => void;
  /** Fired right before a shelf link navigates, so history/transcript persist. */
  onShelfNavigate?: (url: string) => void;

  // Two-agent system props
  currentSearchIntent?: SearchIntent | null;
  isClassifyingIntent?: boolean;
  setIsClassifyingIntent?: React.Dispatch<React.SetStateAction<boolean>>;
  isParallelExecuting?: boolean;
  productTitles?: string[];
  pageId?: string;
  setCurrentSearchIntent?: (intent: SearchIntent | null) => void;
  onSwitchToProductSearch?: () => void;

  // Compact mode - hide title and extra elements
  compactMode?: boolean;

  /** Shopper's admin-1 scope for the live shelf (chip + regional grouping). */
  subregionScope?: ShelfSubregionScope | null;

  // Region selector props
  showRegionSelector?: boolean;
  onRegionConfirm?: (location: UserLocation) => void;
  onRegionSkip?: () => void;
  /** Called when user focuses on search while region selector is open (closes without clearing if reopened via pill) */
  onRegionFocusDismiss?: () => void;
  /** Display label for the currently active region pill (e.g. "Québec") */
  activeRegionLabel?: string | null;
  /** Re-open region selector when user clicks the region pill */
  onReOpenRegionSelector?: () => void;

  // The budget: the composer chip shows the number the search heard, and is its only control
  /** Active numeric budget for this search, or null. */
  budget?: { max: number; min?: number; quantity?: number; stretch?: boolean } | null;
  /** A number typed into the chip (source 'ui' upstream), or null to remove it. */
  onBudgetCommit?: (max: number | null) => void;

  // Product-type search: show loading-clean while Algolia/createSearchMap run (not when redirecting)
  isProductBranchLoading?: boolean;
  // Suppresses the loading container while silently checking Algolia for a possible redirect
  isCheckingAlgoliaForRedirect?: boolean;
  // Product-search parse error: AI returned invalid JSON, show retry option
  productSearchParseError?: boolean;
  onProductSearchRetry?: () => void;
  /** Epoch ms before which Retry is pointless (a 429's window): the pill waits it out. */
  retryNotBefore?: number | null;
  // Tutorial mode props
  tutorialMode?: boolean;
  onSearchFocus?: () => void; // Called when user focuses on search bar (to dismiss tutorial callouts)

  // URL hint: pre-fills search as a dismissable suggestion chip above the bar
  initialQuery?: string;

  // Called when enterPressed but no messages arrive within the safety timeout (stream never started)
  onStreamTimeout?: () => void;

  // Resume a previously persisted chat from a given assistant-turn checkpoint
  onResumeChat?: (args: { transcriptId: string; turnIndex: number }) => void | Promise<void>;
  /** Assistant-message ids rehydrated by resume — those turns skip the typewriter. */
  restoredTurnIds?: ReadonlySet<string>;
  /** Scrollback history: retroactively edit a past answer (or the original query when afterAssistantId is null). */
  onEditHistoryTurn?: (args: { afterAssistantId: string | null; newAnswerText: string }) => void;
}

export const TextAreaSearchBar = ({
  inputRef,

  algoliaMatchs,
  shelfSettledEmpty = false,
  enterPressed,
  header,
  embedded,
  input,
  isDemo,
  isDoneFinal,
  isLoading,
  isSearching,
  locale,
  messages,
  result,
  searchInput,
  seedProduct,
  revealConversationTick = 0,
  askingAbout,
  aiGeneratedQuery,
  browsingLabel = '',
  showNote,
  searchRejectedMessage,
  setSearchRejectedMessage,
  topMenu,

  searchPropositions,
  setSearchPropositions,
  stopSearchProposition,
  setSearchPropositionInput,

  cancelSearch,
  stopSearch,

  setEnterPressed,
  setFocusingOnTextArea,
  setIsDemo,
  setDemoType,
  setIsSearching,
  setSearchInput,
  setMessages,
  setInput,
  setShowNote,

  // Attachments
  attachments = EMPTY_ATTACHMENTS,
  onAddImage,
  onAddLink,
  onAddNote,
  onRemoveAttachment,
  onSubmitAttachmentsOnly,

  // Live results shelf (below the bar)
  onCreatePageFromShelf,
  onShelfNavigate,

  // Two-agent system props
  currentSearchIntent,
  isClassifyingIntent,
  setIsClassifyingIntent,
  isParallelExecuting,
  productTitles,
  pageId,
  setCurrentSearchIntent,
  onSwitchToProductSearch,

  // Compact mode
  compactMode,

  subregionScope = null,

  // Region selector props
  showRegionSelector,
  onRegionConfirm,
  onRegionSkip,
  onRegionFocusDismiss,
  activeRegionLabel,
  onReOpenRegionSelector,

  // The budget
  budget,
  onBudgetCommit,

  // Product-type search
  isProductBranchLoading = false,
  isCheckingAlgoliaForRedirect = false,
  productSearchParseError = false,
  onProductSearchRetry,
  retryNotBefore,
  // Tutorial mode props
  tutorialMode = false,
  onSearchFocus,
  initialQuery,
  onStreamTimeout,
  onResumeChat,
  restoredTurnIds,
  onEditHistoryTurn,
}: TextAreaSearchBarProps) => {
  const ui = useUiContent();
  const searchContent = getSearchUiContent(locale);
  const retryWaitSeconds = useRetryCountdown(retryNotBefore);
  const [isMobileView, setIsMobileView] = useState(false);
  // Matches Tailwind's max-sm: (this config sets sm to 840px), which is the breakpoint
  // main-search-bar uses to collapse the hero on focus.
  const [isPhoneLayout, setIsPhoneLayout] = useState(false);

  // Determine gift-event placeholder on client only to avoid SSR/client time drift.
  const [isGiftEvent, setIsGiftEvent] = useState(false);
  useEffect(() => {
    setIsGiftEvent(isGiftEventPeriod(locale));
  }, [locale]);
  const searchPlaceholder = useMemo(() => {
    if (productTitles && productTitles.length > 0) {
      return searchContent.searchPlaceholderProductPage;
    }
    if (currentSearchIntent) {
      switch (currentSearchIntent) {
        case 'page-question':
          return searchContent.searchPlaceholderQuestion;
        case 'comparison':
          return searchContent.searchPlaceholderComparison;
        case 'recommendation':
          return searchContent.searchPlaceholderRecommendation;
        case 'product-search':
        default:
          return isGiftEvent ? searchContent.searchPlaceholderGiftContext : searchContent.searchPlaceholder;
      }
    }
    return isGiftEvent ? searchContent.searchPlaceholderGiftContext : searchContent.searchPlaceholder;
  }, [productTitles, currentSearchIntent, isGiftEvent, searchContent]);
  /* -----------------------------------------------------------
   * Refs
   * --------------------------------------------------------- */
  const buttonRef = useRef<HTMLButtonElement>(null);
  const voiceButtonRef = useRef<HTMLButtonElement>(null);
  const homeRef = useRef<HTMLAnchorElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const suggestionListRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const voiceButtonContainerRef = useRef<HTMLDivElement>(null);
  const speechToTextRef = useRef<HTMLDivElement>(null);
  const helpPanelRef = useRef<HTMLDivElement>(null);
  const historyButtonRef = useRef<HTMLButtonElement>(null);
  // The "+" menu and the composer's "+" button (the classic bar's "+" is buttonRef).
  const attachMenuRef = useRef<HTMLDivElement>(null);
  const attachButtonRef = useRef<HTMLButtonElement>(null);
  // Whether the search field had focus when the "+" menu opened: focus goes
  // back there on close only then (a resting phone bar must not summon the keyboard).
  const attachMenuHadFocusRef = useRef(false);
  const capsuleRef = useRef<HTMLDivElement>(null);
  // The textarea + footer rows only (a real box in composer layout, `contents`
  // in the classic pill). What the phone keyboard policy centres — the card's
  // conversation region above it must not pull the "centre" up into the thread.
  const composerRowRef = useRef<HTMLDivElement>(null);
  // The row that holds the capsule. Its top is where the card would start with an
  // empty region, so it never moves with the card (see the hero-slot clamp below).
  const barRowRef = useRef<HTMLDivElement>(null);
  // Guards the stash-driven auto-resume below against firing twice (React
  // StrictMode double-invokes effects in dev; onResumeChat is not idempotent).
  const searchReturnStashConsumedRef = useRef(false);

  /* -----------------------------------------------------------
   * State
   * --------------------------------------------------------- */
  const [highlightedIndex, setHighlightedIndex] = useState<number | undefined>(undefined);
  const [isFocused, setIsFocused] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showHelpOptions, setShowHelpOptions] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isSwapped, setIsSwapped] = useState(false);
  const [isSpeechProcessing, setIsSpeechProcessing] = useState(false);
  const [isVoiceListening, setIsVoiceListening] = useState(false);
  // True from "camera button tapped" until the native picker resolves (file
  // chosen or cancelled). Holds the phone composer open while the OS sheet
  // has document focus — see the picker-cancel effect near openImagePicker.
  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);
  // The "+" menu (photo, link, note). Opened by keyboard: focus moves into it.
  const [isAttachMenuOpen, setIsAttachMenuOpen] = useState(false);
  const [attachMenuFromKeyboard, setAttachMenuFromKeyboard] = useState(false);
  const attachMenuId = useId();
  const hasAttachments = attachments.length > 0;
  // The placeholders a pasted link leaves in the sentence are paired with the
  // link chips by label (shared/src/utils/link-tokens.ts).
  const linkItems = useMemo(
    () => attachments.filter((a): a is Extract<ComposerAttachment, { kind: 'link' }> => a.kind === 'link'),
    [attachments],
  );
  const linkLabels = useMemo(() => linkItems.map((link) => linkTokenLabel(link.url)), [linkItems]);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  // Streaming transcript painted while the mic is live (Web Speech preview).
  // Empty on browsers without SpeechRecognition — the centered wave owns the
  // row there instead. Cleared the moment listening ends.
  const [liveTranscript, setLiveTranscript] = useState('');
  // Imperative stop handed up by VoiceSearchButton, so the big stop bar / stop
  // pill can end the recording without living inside the button component.
  const voiceControlRef = useRef<VoiceSearchControl | null>(null);
  const [isVoiceInput, setIsVoiceInput] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusAttention, setFocusAttention] = useState(false);

  // "Pick up where you left off" chip: the most recent UNFINISHED persisted chat
  // (no finalHref — the user never reached a results page). Loaded once per mount,
  // so a refresh mid-conversation always resurfaces it — unless the user closed it
  // with the X, which is persisted on the transcript (resumeDismissedAt) and
  // survives refreshes. Label = the latest refined query of that conversation.
  const [resumeCandidate, setResumeCandidate] = useState<{
    transcriptId: string;
    turnIndex: number;
    label: string;
  } | null>(null);
  const [isResumeDismissed, setIsResumeDismissed] = useState(false);
  // Debounced visibility: true only once every other condition has held for
  // RESUME_CHIP_DELAY_MS straight (see the effect near the JSX return below).
  const [showResumeChip, setShowResumeChip] = useState(false);
  useEffect(() => {
    if (topMenu) return; // main bar only — the header keeps its help-panel entry point

    // A result link (product card, "see full page", alternate pill, created page,
    // kit) leaves a sessionStorage breadcrumb right before navigating away — see
    // lib/search-return-stash.ts. Every one of those exits is a soft client-side
    // navigation, so a plain Back is just a same-document popstate: the
    // performance-navigation-timing check below (which only sees real document
    // loads) never catches it. The stash does. Gated on `initialQuery` (the `?q`
    // param the search page URL carries once a search has run) so a fresh visit
    // with no query in the URL — e.g. via the logo — never resurrects a stash
    // left over from an unrelated earlier session. And only when that `?q` is
    // the stashed conversation's own: a new search arriving with other words
    // (`?q=…&autoSearch=true` from the extension, say) runs instead.
    if (!searchReturnStashConsumedRef.current && onResumeChat && initialQuery) {
      const stash = readSearchReturnStash(SEARCH_RETURN_WINDOW_MS);
      if (stash) {
        const transcript = getChatTranscript(stash.transcriptId);
        if (
          transcript &&
          transcript.turns.length > 0 &&
          transcript.locale === locale.path &&
          queryBelongsToTranscript(initialQuery, transcript)
        ) {
          const lastTurn = transcript.turns[transcript.turns.length - 1];
          // A concluded search that never got a shelf or a destination is
          // the loading-theater dead state (isDone, no results, page
          // creation in flight). Auto-resuming it on `?q=` puts the shopper
          // right back in that spinner. Leave the chip; a click refetches.
          const brokenConclusion =
            !!lastTurn.parsed.isDone &&
            !transcript.finalHref &&
            !(transcript.finalShelf && transcript.finalShelf.length > 0);
          if (!brokenConclusion) {
            searchReturnStashConsumedRef.current = true;
            void onResumeChat({ transcriptId: transcript.id, turnIndex: lastTurn.index });
            return;
          }
        }
      }
    }

    const transcripts = getChatTranscripts(locale.path).filter((t) => t.turns.length > 0);
    if (transcripts.length === 0) return;

    // Back/forward onto the search page — including the browser Back that leaves a
    // page this search sent us to — restores the conversation outright instead of
    // showing a bare bar. Recency-gated so an old chat never resurrects unbidden.
    const navEntry = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    const cameBack = navEntry?.type === 'back_forward';
    const latest = transcripts[0];
    const latestLastTurn = latest.turns[latest.turns.length - 1];
    if (
      cameBack &&
      onResumeChat &&
      Date.now() - latest.updatedAt < RESUME_ON_BACK_WINDOW_MS
    ) {
      const brokenConclusion =
        !!latestLastTurn.parsed.isDone &&
        !latest.finalHref &&
        !(latest.finalShelf && latest.finalShelf.length > 0);
      if (!brokenConclusion) {
        void onResumeChat({ transcriptId: latest.id, turnIndex: latestLastTurn.index });
        return;
      }
    }

    // Otherwise offer the chip — but only for a conversation that never concluded
    // and whose chip the user hasn't explicitly closed before.
    const unfinished = transcripts.find((t) => !t.finalHref && !t.resumeDismissedAt);
    if (!unfinished) return;
    const lastTurn = unfinished.turns[unfinished.turns.length - 1];
    const label = (lastTurn.parsed.searchQuery || unfinished.initialUserInput || '').trim();
    if (!label) return;
    setResumeCandidate({
      transcriptId: unfinished.id,
      turnIndex: lastTurn.index,
      label,
    });
    // onResumeChat is intentionally omitted: it's a plain (non-memoized) function
    // recreated on every parent render, and this effect must run once per mount
    // (guarded by searchReturnStashConsumedRef), not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale.path, topMenu, initialQuery]);

  // Product-search Q&A flow active: the bar renders as a two-row composer capsule
  // (textarea on top, filter chips + mic + send in a footer row).
  const isProductSearchStarted = searchPropositions.length === 0 && messages.length > 0 && !isMinimized;

  // Phones only: focusing the bar morphs it into the same composer capsule the Q&A
  // flow uses, which brings "add an image" and the price chip within reach *before*
  // the first query instead of hiding them until a search has started. The bar
  // stays exactly where it is while this happens — only its own two rows change.
  //
  // Held in state rather than derived straight from isFocused so that tapping one of
  // the footer affordances — which blurs the textarea — cannot unmount the very
  // control being tapped. See the grace-period effect below.
  const [focusComposerOpen, setFocusComposerOpen] = useState(false);

  // The native picker blurs the textarea; whether we hand focus back afterwards
  // depends on whether the user was typing when they reached for the camera.
  // Restoring it unconditionally summons the keyboard over a bar nobody asked to
  // type in.
  const pickerHadFocusRef = useRef(false);

  // The two-row capsule layout switch (`isComposerLayout`) is declared further
  // down, next to the card-top gates it now depends on — see "Card top region".

  // Constants
  const TEXTAREA_HEIGHT = '2.7rem';
  const STOP_ECHO_MS = 500;

  // Once a search is locked in the bar stops showing what the user typed and shows
  // the AI-rewritten query instead, which is usually longer. Autosizing has to follow
  // whatever is actually rendered — keying it off searchInput alone leaves the box at
  // the typed text's height and overflow-y-hidden swallows the extra lines.
  const textareaValue = isDoneFinal ? aiGeneratedQuery || input : searchInput;

  // Rotating example placeholder — the idle bar teaches by showing real
  // queries instead of the old demo buttons' meta-descriptions. Stops the
  // instant anything else is going on, and hides entirely once the bar is
  // focused (see hiddenWhileFocused below) so nothing moves while the user is
  // about to type — the examples live in the chips underneath at that point.
  const exampleQueries = useMemo(
    () => getSearchExamples(locale),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale.languageCode],
  );
  const ghostEnabled =
    searchInput.length === 0 &&
    !isFocused &&
    !isVoiceListening &&
    !isSpeechProcessing &&
    messages.length === 0 &&
    !isSearching &&
    !isLoading &&
    !isDoneFinal &&
    !isClassifyingIntent &&
    !showRegionSelector &&
    !compactMode &&
    !header;
  const { placeholder: ghostPlaceholder, fadingOut: ghostFadingOut } = useRotatingPlaceholder({
    enabled: ghostEnabled,
    texts: exampleQueries,
    staticText: searchPlaceholder,
  });
  // Focused + empty: no placeholder at all, so the field reads as ready to type. Unless
  // the shopper came from a product's chip: then it names what they are asking about.
  const idlePlaceholder = askingAbout
    ? ` ${searchContent.askAboutProductPlaceholder.replace('{product}', askingAbout)}`
    : isFocused && searchInput.length === 0
      ? ''
      : ` ${ghostPlaceholder}`;

  /* -----------------------------------------------------------
   * Effect: Detect Mobile Viewport
   * --------------------------------------------------------- */
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
      setIsMobileView(window.innerWidth < 640); // sm breakpoint
      setIsPhoneLayout(window.innerWidth < 840); // Tailwind max-sm: (sm is 840px here)
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  /* -----------------------------------------------------------
   * Phones: the search block rises with the keyboard, and comes back down
   * --------------------------------------------------------- */
  // Phones report a shrinking `visualViewport` as the keyboard slides up while
  // the layout viewport stays put — the only reliable signal for "how much
  // screen is actually free". When that happens under the composer, the whole
  // search block (`#main-search-bar`: wordmark, composer, examples, shelf) is
  // LIFTED so the composer sits in the middle of the free strip — or, once a
  // question is on the card, so the whole card reads between header and
  // keyboard. When the keyboard leaves (a search was sent, the bar was left)
  // the block comes back down. No keyboard, no move.
  //
  // The lift is one CSS transform transition. The page is NOT scrolled:
  //  - scripted scrolling under a phone keyboard reaches the screen a few
  //    frames late on iOS, and `scrollY` / `visualViewport.pageTop` read stale
  //    in between. A glide that looked at where the page was (this was one)
  //    chased its own echo, and the bar shuddered up and down on real phones
  //    while the simulator, with less lag, looked fine;
  //  - the browser scrolls a focused field into view by itself when the
  //    keyboard would cover its caret. A lift never writes a scroll position,
  //    so the two add up instead of fighting;
  //  - with the page where it was, there is nothing to scroll back after a
  //    search: the bar is simply mid-screen again.
  //
  // The in-page composer only. The header bar and the phone bottom sheet are
  // fixed to the screen and position themselves.
  const heroAnchored = !topMenu && !header;
  // On a phone the discussion thread fills the screen between the header and
  // the bottom (or the keyboard). Declared ahead of the lift below on purpose:
  // both listen to the visual viewport, and the lift must find the thread
  // already sized for the keyboard. Also owns `--card-top-h`.
  const handleCardTopHeight = usePhoneThreadFit({
    capsuleRef,
    barRowRef,
    composerRowRef,
    enabled: isPhoneLayout && heroAnchored,
  });
  // The foot of the card where the shelf puts its "Might interest you" bar.
  const [peekHost, setPeekHost] = useState<HTMLDivElement | null>(null);
  // The budget chip's field is open (it takes focus from the textarea by design).
  const [isEditingBudget, setIsEditingBudget] = useState(false);
  const budgetUi = getBudgetUiContent(locale.languageCode);
  // The triggers that live in React state (focus, blur, a turn being sent)
  // reach the viewport logic through this; null off phones and off the hero.
  const phoneViewportRef = useRef<{
    composerFocused: () => void;
    composerBlurred: () => void;
    turnSent: () => void;
  } | null>(null);
  // Interactions that borrow the focus mid-composition: while the keyboard is
  // still up, the block stays where it is until they finish.
  const keyboardLiftHeldRef = useRef(false);
  keyboardLiftHeldRef.current =
    isEditingBudget ||
    (isImagePickerOpen && pickerHadFocusRef.current) ||
    showHelpOptions ||
    isAttachMenuOpen ||
    isVoiceListening ||
    isSpeechProcessing;

  useEffect(() => {
    if (!isPhoneLayout || !heroAnchored) return;
    const viewport = window.visualViewport;
    if (!viewport) return;

    // How far the block is lifted (or being lifted) — the transition's target.
    let lift = 0;
    let lifted: HTMLElement | null = null;
    // Keyboard inset the composer was last placed for; null = not in this keyboard episode.
    let placedInset: number | null = null;
    let keyboardOpen = false;
    let lastVisualHeight = viewport.height;
    // Where the page stood before the keyboard, and whether the shopper has
    // scrolled it since: only a scroll the BROWSER made is put back.
    let standingTop: number | null = null;
    let userScrolled = false;
    let stopScrollBack: (() => void) | null = null;
    let settleId: ReturnType<typeof setTimeout> | undefined;
    let closedId: ReturnType<typeof setTimeout> | undefined;
    let restId: ReturnType<typeof setTimeout> | undefined;
    let checkId: ReturnType<typeof setTimeout> | undefined;
    let caretId: ReturnType<typeof setTimeout> | undefined;
    let touchDown = false;
    let touchStartY = 0;
    let touchStartTop = 0;
    let afterTouch: (() => void) | null = null;

    const visualTop = () => viewport.pageTop;
    const keyboardInset = () => keyboardInsetPx(window.innerHeight, viewport.height, viewport.scale);
    const keyboardUp = () => keyboardInset() > KEYBOARD_OPEN_THRESHOLD_PX;
    // The DOM, not `isFocused`: a textarea disabled mid-commit loses focus
    // without React's onBlur on older Chromium.
    const composerHasKeyboard = () => {
      const el = inputRef.current;
      return !!el && document.activeElement === el && !el.disabled;
    };
    const editableFocused = () => {
      const el = document.activeElement as HTMLElement | null;
      return !!el && (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT' || el.isContentEditable);
    };
    const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const searchBlock = () => capsuleRef.current?.closest<HTMLElement>('#main-search-bar') ?? null;

    const dropLiftStyles = () => {
      if (!lifted) return;
      lifted.style.transition = '';
      lifted.style.transform = '';
      lifted.style.willChange = '';
      lifted = null;
    };
    // iOS draws the text caret itself, from a position the page reports now
    // and then — not on every frame of a transform. Under a moving block it is
    // left behind for a few frames, blinking in mid-air below the bar. It sits
    // the move out and comes back once the field has arrived.
    const restCaret = () => {
      clearTimeout(caretId);
      if (inputRef.current) inputRef.current.style.caretColor = '';
    };
    const hideCaretWhileMoving = () => {
      const el = inputRef.current;
      if (!el) return;
      clearTimeout(caretId);
      el.style.caretColor = 'transparent';
      caretId = setTimeout(restCaret, KEYBOARD_LIFT_MS + 60);
    };
    const setLift = (px: number) => {
      const block = searchBlock();
      if (!block || px === lift) return;
      if (lifted && lifted !== block) dropLiftStyles();
      lifted = block;
      lift = px;
      clearTimeout(restId);
      const instant = reducedMotion();
      if (!instant) hideCaretWhileMoving();
      block.style.willChange = 'transform';
      block.style.transition = instant ? 'none' : `transform ${KEYBOARD_LIFT_MS}ms ${KEYBOARD_LIFT_EASING}`;
      block.style.transform = `translateY(${-px}px)`;
      // Back at rest the transform comes off altogether: a transformed
      // ancestor re-anchors every fixed element inside it.
      if (px === 0) restId = setTimeout(dropLiftStyles, instant ? 0 : KEYBOARD_LIFT_MS + 80);
    };

    // The card and its composer row in the document, as laid out: with the
    // block's own transform taken back out, and with whatever is still
    // animating above them (the hero copy collapsing on a first focus)
    // already at its end.
    const measureComposer = () => {
      const capsule = capsuleRef.current;
      if (!capsule) return null;
      const block = searchBlock();
      return readSettledLayout(block ?? capsule, () => {
        const shift = block ? new DOMMatrixReadOnly(getComputedStyle(block).transform).m42 : 0;
        const docTop = document.documentElement.getBoundingClientRect().top;
        const card = capsule.getBoundingClientRect();
        // Composer rows when they have a box of their own; the classic pill IS the
        // capsule (its row wrapper is display:contents and measures 0×0).
        const rowBox = composerRowRef.current?.getBoundingClientRect();
        const row = rowBox && rowBox.height > 0 ? rowBox : card;
        return { cardTop: card.top - docTop - shift, rowTop: row.top - docTop - shift, rowHeight: row.height };
      });
    };

    const composition = () => {
      const m = measureComposer();
      return (
        m && {
          ...m,
          visualHeight: viewport.height,
          headerBand: parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0,
        }
      );
    };
    // How much further the block has to rise for the composition; 0 = close
    // enough. The lift already applied counts as the strip sitting that much lower.
    const riseNeeded = (first: boolean) => {
      const c = composition();
      return c ? keyboardPlacementDelta({ ...c, visualTop: visualTop() + lift, first }) : 0;
    };
    // The same without any tolerance: how far off its exact mark the composer is.
    const exactRiseNeeded = () => {
      const c = composition();
      return c ? keyboardCompositionTop(c) - (visualTop() + lift) : 0;
    };
    const placeComposer = (first: boolean) => {
      if (!keyboardUp() || !composerHasKeyboard()) return;
      const rise = riseNeeded(first);
      placedInset = keyboardInset();
      clearTimeout(checkId);
      if (rise === 0) return;
      setLift(Math.round(lift + rise));
      // Aimed once, from a layout read in the middle of everything else that a
      // focus sets in motion. Look again when it has all come to rest and take
      // up whatever is left, rather than leave the composer off its mark.
      checkId = setTimeout(() => {
        if (!keyboardUp() || !composerHasKeyboard() || touchDown) return;
        const rest = Math.round(exactRiseNeeded());
        if (Math.abs(rest) > LIFT_CHECK_MIN_PX) setLift(lift + rest);
      }, KEYBOARD_LIFT_MS + LIFT_CHECK_AFTER_MS);
    };
    // Nothing moves under a finger that is still down: the tap's click lands
    // where the finger lifts, and the block must not have moved under it.
    const schedulePlacement = (first: boolean, waitMs: number) => {
      clearTimeout(settleId);
      const run = () => {
        if (touchDown) afterTouch = () => placeComposer(first);
        else placeComposer(first);
      };
      if (waitMs > 0) settleId = setTimeout(run, waitMs);
      else run();
    };

    const lowerComposer = () => {
      clearTimeout(settleId);
      clearTimeout(checkId);
      afterTouch = null;
      placedInset = null;
      setLift(0);
      // The browser scrolls a field into view by itself when the keyboard
      // would cover its caret, and leaves the page there. Put back what it
      // moved — not what the shopper scrolled.
      const from = standingTop;
      standingTop = null;
      if (from === null || userScrolled) return;
      const moved = visualTop() - from;
      if (Math.abs(moved) <= CONVERSATION_RETURN_MIN_PX) return;
      stopScrollBack?.();
      stopScrollBack = glideWindowScrollTo(window.scrollY - moved);
    };

    const keyboardClosed = () => {
      keyboardOpen = false;
      lowerComposer();
    };
    const onViewportResize = () => {
      const previousHeight = lastVisualHeight;
      lastVisualHeight = viewport.height;
      if (keyboardUp()) {
        clearTimeout(closedId);
        keyboardOpen = true;
        if (!composerHasKeyboard()) return;
        if (placedInset === null) {
          // Reduced motion gets one jump once the keyboard has settled, not one per step.
          if (!isKeyboardLeaving(previousHeight, viewport.height)) {
            schedulePlacement(true, reducedMotion() ? KEYBOARD_SETTLE_MS : 0);
          }
        } else if (shouldAttemptRecenter(placedInset, keyboardInset())) {
          // The keyboard changed size (a second step, a keyboard swap, a
          // rotation): wait for it to settle, then move only for a real miss.
          schedulePlacement(false, KEYBOARD_SETTLE_MS);
        }
        return;
      }
      if (!keyboardOpen) return;
      clearTimeout(settleId);
      clearTimeout(closedId);
      // Android can read "closed" for a frame while a keyboard is still coming
      // up under a focused field; without a focused field there is no doubt.
      if (composerHasKeyboard()) closedId = setTimeout(keyboardClosed, KEYBOARD_CLOSED_HOLD_MS);
      else keyboardClosed();
    };

    const noteStandingPosition = () => {
      standingTop = visualTop();
      userScrolled = false;
    };
    const composerFocused = () => {
      if (!keyboardOpen && lift === 0) noteStandingPosition();
      // The keyboard can already be up: it was serving another field, or
      // WebKit sent its one opening resize ahead of the focus event.
      if (keyboardUp() && placedInset === null) {
        keyboardOpen = true;
        schedulePlacement(true, reducedMotion() ? KEYBOARD_SETTLE_MS : 0);
      }
    };
    // The textarea lost the focus. The keyboard is on its way out unless
    // another field took it or an interaction only borrowed it: start down
    // now, with the keyboard, rather than when the viewport reports it gone.
    const composerBlurred = () => {
      if (lift === 0 && standingTop === null) return;
      if (
        shouldLowerComposer({
          keyboardUp: keyboardUp(),
          editableFocused: editableFocused(),
          held: keyboardLiftHeldRef.current,
        })
      ) {
        lowerComposer();
      }
    };
    // A turn was sent: the textarea is disabled from here and the keyboard goes.
    const turnSent = () => {
      if (lift !== 0 || keyboardUp()) lowerComposer();
    };

    const onTouchStart = (event: TouchEvent) => {
      touchDown = true;
      touchStartY = event.touches[0]?.clientY ?? 0;
      touchStartTop = visualTop();
      // A keyboard can come back with no focus event (Android's back gesture,
      // then a tap on the bar that never lost focus): the tap is the one
      // moment to note where the page stands.
      if (!keyboardOpen && lift === 0) noteStandingPosition();
    };
    const onTouchMove = (event: TouchEvent) => {
      if (userScrolled || standingTop === null) return;
      const travelled = Math.abs((event.touches[0]?.clientY ?? touchStartY) - touchStartY);
      if (travelled > GESTURE_SCROLL_PX && Math.abs(visualTop() - touchStartTop) > FOREIGN_SCROLL_MIN_PX) {
        userScrolled = true;
      }
    };
    const onTouchEnd = (event: TouchEvent) => {
      touchDown = event.touches.length > 0;
      if (touchDown || !afterTouch) return;
      const run = afterTouch;
      afterTouch = null;
      // A frame later: the click this touch produces is dispatched first.
      requestAnimationFrame(run);
    };
    const onWheel = () => {
      if (standingTop !== null) userScrolled = true;
    };

    phoneViewportRef.current = { composerFocused, composerBlurred, turnSent };
    viewport.addEventListener('resize', onViewportResize);
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchEnd, { passive: true });
    window.addEventListener('wheel', onWheel, { passive: true });
    return () => {
      phoneViewportRef.current = null;
      stopScrollBack?.();
      clearTimeout(settleId);
      clearTimeout(closedId);
      clearTimeout(restId);
      clearTimeout(checkId);
      restCaret();
      dropLiftStyles();
      viewport.removeEventListener('resize', onViewportResize);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
      window.removeEventListener('wheel', onWheel);
    };
  }, [isPhoneLayout, heroAnchored, inputRef]);

  // Focus: note where the page stands, and catch a keyboard that is already
  // up. The focus edge only — a panel opening over a bar that keeps its focus
  // is not a new focus.
  useEffect(() => {
    if (!isPhoneLayout || !heroAnchored || !isFocused) return;
    phoneViewportRef.current?.composerFocused();
  }, [isFocused, isPhoneLayout, heroAnchored]);

  // Blur: two frames give whatever took the focus (the budget field, a panel,
  // the picker) time to say so, and the tap that caused the blur has landed by
  // then, so the block cannot move a control out from under it. A hold that
  // ends later re-runs this and the block comes down then.
  useEffect(() => {
    if (!isPhoneLayout || !heroAnchored || isFocused) return;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => phoneViewportRef.current?.composerBlurred());
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [
    isFocused,
    isPhoneLayout,
    heroAnchored,
    isEditingBudget,
    isImagePickerOpen,
    showHelpOptions,
    isAttachMenuOpen,
    isVoiceListening,
    isSpeechProcessing,
  ]);

  // A turn was sent (Enter, the send button, a chip, dictation, an edited
  // turn). The rising edge only — when the answer finishes streaming there is
  // nothing to do.
  const turnInFlight = isLoading || !!isClassifyingIntent || isProductBranchLoading;
  const turnWasInFlightRef = useRef(turnInFlight);
  useEffect(() => {
    const wasInFlight = turnWasInFlightRef.current;
    turnWasInFlightRef.current = turnInFlight;
    if (turnInFlight && !wasInFlight) phoneViewportRef.current?.turnSent();
  }, [turnInFlight]);

  /* -----------------------------------------------------------
   * Effect: Close the focus composer once the bar is truly idle
   * --------------------------------------------------------- */
  // Collapsing the instant the textarea blurs would pull the footer out from under
  // a finger that is mid-tap on one of its own chips (mousedown blurs before click
  // lands). Wait out a short grace period and keep the capsule open for anything
  // that means the user is still composing: typed text, an attached image, or an
  // open price/region selector.
  useEffect(() => {
    if (!focusComposerOpen || isFocused) return;
    if (
      shouldHoldComposerOpen({
        searchInput,
        hasAttachments,
        isAttachMenuOpen,
        isEditingBudget,
        showRegionSelector: !!showRegionSelector,
        isVoiceListening,
        isSpeechProcessing,
        isImagePickerOpen,
        showHelpOptions,
      })
    ) {
      return;
    }
    const timer = window.setTimeout(() => setFocusComposerOpen(false), 300);
    return () => clearTimeout(timer);
  }, [
    focusComposerOpen,
    isFocused,
    searchInput,
    hasAttachments,
    isAttachMenuOpen,
    isEditingBudget,
    showRegionSelector,
    isVoiceListening,
    isSpeechProcessing,
    isImagePickerOpen,
    showHelpOptions,
  ]);

  /* -----------------------------------------------------------
   * Effect: Region panel takes over from the keyboard
   * --------------------------------------------------------- */
  // The region map grows upward from the bar's top edge and wants most of a
  // phone screen, which an open keyboard is busy occupying — so dismiss it on
  // the panel's opening edge. A blur and nothing else: the bar does not move.
  const panelWasOpenRef = useRef(false);
  useEffect(() => {
    const panelOpen = !!showRegionSelector;
    const opening = panelOpen && !panelWasOpenRef.current;
    panelWasOpenRef.current = panelOpen;
    if (!opening) return;
    // messages.length inline rather than the messageIsNotEmpty alias below —
    // that const is declared further down the component and would be in its
    // temporal dead zone when this dependency array is built.
    if (
      !shouldDismissKeyboardForPanel({
        isPhoneLayout,
        topMenu: !!topMenu,
        messageIsNotEmpty: messages.length > 0,
        isLoading,
        showRegionSelector: !!showRegionSelector,
      })
    ) {
      return;
    }
    inputRef.current?.blur();
  }, [showRegionSelector, isPhoneLayout, topMenu, messages.length, isLoading, inputRef]);

  // A phone that rotates to a wide layout should drop back to the classic bar.
  useEffect(() => {
    if (!isPhoneLayout) {
      setFocusComposerOpen(false);
    }
  }, [isPhoneLayout]);

  /* -----------------------------------------------------------
   * Effect: Debounce Search Input
   * --------------------------------------------------------- */
  const [debouncedSearchInput] = useDebounce(searchInput, 300);

  /* -----------------------------------------------------------
   * Effect: Handle Click Outside & Debounced Search Trigger
   * --------------------------------------------------------- */
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      // Evaluate containment SYNCHRONOUSLY, while event.target is still mounted.
      // A clicked help-panel option (e.g. "Search history", "Past chats") switches the
      // panel to a sub-view via its onClick, which unmounts the clicked button before the
      // deferred work below runs. A late contains() check on that now-detached node would
      // falsely report "clicked outside" and collapse the panel — making the menu close the
      // instant you pick any option. Capturing the booleans here keeps the check accurate.
      const target = event.target as Node;

      const clickedOutsideHelp =
        !suggestionListRef.current?.contains(target) &&
        !messagesRef.current?.contains(target) &&
        !helpPanelRef.current?.contains(target) &&
        !historyButtonRef.current?.contains(target) &&
        // The "+" menu's History row opens the panel on its own click.
        !attachMenuRef.current?.contains(target) &&
        inputRef.current &&
        !inputRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        !speechToTextRef.current?.contains(target) &&
        !voiceButtonContainerRef.current?.contains(target) &&
        (!homeRef.current || (homeRef.current && !homeRef.current.contains(target)));

      // The "+" menu closes on any press outside it and its two buttons
      // (each button toggles it on its own click).
      const clickedOutsideAttach =
        !attachMenuRef.current?.contains(target) &&
        !attachButtonRef.current?.contains(target) &&
        !buttonRef.current?.contains(target);

      // Close messages menu when clicking outside (for both product search and page question)
      // Don't close if clicking on voice/speech buttons
      // Don't close if user is in research mode (active conversation that hasn't completed)
      // Don't close if loading steps are visible (isDoneFinal without result means loading animation is showing)
      const shouldCollapseMessages =
        messages.length > 0 &&
        !messagesRef.current?.contains(target) &&
        !inputRef.current?.contains(target) &&
        !capsuleRef.current?.contains(target) &&
        !attachMenuRef.current?.contains(target) &&
        !speechToTextRef.current?.contains(target) &&
        !voiceButtonContainerRef.current?.contains(target) &&
        !isMinimized &&
        isDoneFinal && // Only allow collapsing when search is complete
        result; // Don't collapse while loading steps are animating (no result yet)

      // Defer only the state changes so the button's own onClick (a later click event)
      // still lands before the panel/menu unmounts.
      setTimeout(() => {
        if (clickedOutsideHelp) {
          setShowHelpOptions(false);
        }
        if (clickedOutsideAttach) {
          setIsAttachMenuOpen(false);
        }

        if (shouldCollapseMessages) {
          // Collapse the menu by putting query in search bar and hiding messages
          const queryToRestore = aiGeneratedQuery || input || searchInput;
          setSearchInput(queryToRestore);
          setIsMinimized(true);
        }
      }, 0);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [inputRef, messages.length, isMinimized, aiGeneratedQuery, input, searchInput, isDoneFinal, result]);

  // A conversation the bar opened by itself must be on screen: re-focusing is
  // the only other way out of a minimized panel, and nothing asked the shopper
  // to. Deferred so it lands after the collapse that the same press, landing
  // outside the panel, scheduled above.
  useEffect(() => {
    if (!revealConversationTick) return;
    const timer = setTimeout(() => setIsMinimized(false), 0);
    return () => clearTimeout(timer);
  }, [revealConversationTick]);

  useEffect(() => {
    // If we have messages from an active conversation, keep isSearching true
    if (messages && messages.length > 0) {
      setIsSearching(true);
      return;
    }

    // If currently loading, keep isSearching true
    if (isLoading) {
      setIsSearching(true);
      return;
    }

    if (!debouncedSearchInput) {
      setIsSearching(false);
      return;
    }

    // Don't trigger search propositions if topMenu is true
    if (topMenu) {
      setIsSearching(true);
      return;
    }

    // CRITICAL: Don't trigger new propositions if enterPressed is true (search already initiated)
    // This prevents race condition where propositions stream continues after user presses Enter
    if (enterPressed) {
      return;
    }

    // Don't trigger search propositions if input is from voice search
    if (isVoiceInput) {
      setIsSearching(true);
      return;
    }

    // Nothing to submit here. Search propositions are switched off (their list below is
    // commented out) and their useChat in search-bar.tsx has no endpoint: `api: undefined`
    // makes the AI SDK POST to /api/chat, which does not exist. Every typed query on the
    // home page logged that 404 and left a stray message in the propositions, which squared
    // the bar's corners for a dropdown that never renders.
    setIsSearching(true);
  }, [debouncedSearchInput, messages.length, isLoading, enterPressed, isVoiceInput]); // eslint-disable-line react-hooks/exhaustive-deps

  /* -----------------------------------------------------------
   * Effect: Stop propositions when Enter is pressed (CRITICAL for race condition fix)
   * 
   * RACE CONDITION FIX:
   * When user presses Enter while propositions are still streaming, we had a race condition:
   * 1. User presses Enter → setSearchPropositions([]) is called
   * 2. But the stream is still active and keeps adding new propositions
   * 3. Result: propositionsLength > 0, blocking the messages modal from showing
   * 
   * This effect immediately stops the stream and clears propositions when enterPressed = true,
   * preventing any new propositions from being added after search initiation.
   * --------------------------------------------------------- */
  useEffect(() => {
    if (enterPressed) {
      // Immediately stop the proposition stream when user initiates search
      stopSearchProposition();
      // Clear any existing propositions to prevent them from blocking the messages modal
      setSearchPropositions([]);
    }
  }, [enterPressed, stopSearchProposition, setSearchPropositions]);

  /* -----------------------------------------------------------
   * Effect: Reset isSubmitting when enterPressed goes back to false (cancel/reset)
   * Ensures cancel search or reset clears the submitting flag so user can search again
   * --------------------------------------------------------- */
  useEffect(() => {
    if (!enterPressed) {
      setIsSubmitting(false);
    }
  }, [enterPressed]);

  /* -----------------------------------------------------------
   * Safety timeout: hand the composer back if nothing comes back for the
   * sent search within STREAM_START_TIMEOUT_MS (network/API failure). A
   * named-product search never streams a turn; its own signs of life keep
   * the timer quiet (stream-start-timeout.ts).
   * --------------------------------------------------------- */
  useStreamStartTimeout(
    {
      enterPressed,
      messageCount: messages.length,
      isClassifyingIntent: !!isClassifyingIntent,
      browsing: browsingLabel.length > 0,
      isCheckingAlgoliaForRedirect,
      isProductBranchLoading,
    },
    () => {
      setEnterPressed(false);
      onStreamTimeout?.();
    },
  );

  /* -----------------------------------------------------------
   * Effect: Trigger New Search When Input Prop Updates (e.g. via suggestion click)
   * --------------------------------------------------------- */
  useEffect(() => {
    if (input) {
      stopSearchProposition();
      setSearchPropositions([]);
    }
  }, [input]); // eslint-disable-line react-hooks/exhaustive-deps

  /* -----------------------------------------------------------
   * Helper Functions for Two-Agent System
   * --------------------------------------------------------- */
  // (The "Autosize & Initialize Input" effect lives below the card-top gates —
  // it reads `isComposerLayout`, which is declared there.)

  // Handle search button click or suggestion selection
  const waitForValidSuggestion = async () => {
    const maxWaitTime = 2000;
    const pollInterval = 200;
    const startTime = Date.now();
    let suggestions;

    while (Date.now() - startTime < maxWaitTime) {
      const currentPropositions = searchPropositions;
      
      if (currentPropositions && currentPropositions.length > 1 && currentPropositions[1].content) {
        suggestions = parseSuggestionsMessage(currentPropositions[1]);
        
        if (suggestions && suggestions.length > 0) {
          const suggestion = suggestions[0];
          const suggestionIsLongEnough = suggestion.length >= MIN_SUBCATEGORIES_FOR_LIST;
          
          if (suggestionIsLongEnough) {
            return suggestions;
          }
        }
      }
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }
    // Return the suggestions even if incomplete after waiting the max time.
    return suggestions;
  };

  /**
   * Hand words to the search pipeline (search-bar's input effect). That effect
   * fires on a CHANGE of `input`, so words identical to the last submission
   * did nothing and left the spinner on: the case after a failed turn hands
   * the shopper's words back for another try, and the case of the same answer
   * to two questions in a row. Clear first, the bounce the Skip chip uses.
   */
  const submitInput = (value: string) => {
    if (value === input) {
      setInput('');
      setTimeout(() => setInput(value), 0);
      return;
    }
    setInput(value);
  };

  const pressSearch = async (textareaSource?: boolean) => {
    try {
      // Prevent multiple simultaneous submissions
      if (isSubmitting) {
        console.log('[pressSearch] Already submitting, ignoring duplicate click');
        return;
      }

      // Nothing typed and nothing attached: nothing to send.
      if (!searchInput && !hasAttachments) {
        return;
      }
      // A typed address becomes a link chip, like a pasted one, and leaves its
      // placeholder where it was written ("something like ‹Garmin Fenix 8›").
      // Sent alone, a link is a question in itself and goes without words.
      const results = new Map<string, AddAttachmentResult>();
      for (const link of scanLinks(searchInput)) {
        if (!results.has(link.url)) results.set(link.url, onAddLink?.(link.url) ?? 'invalid');
      }
      const linkAdded = [...results.values()].some((r) => r === 'added' || r === 'duplicate');
      const labelsBefore = new Set(findLinkTokens(searchInput, linkLabels).map((r) => r.label));
      const withPlaceholders = linkAdded
        ? replaceLinksInText(searchInput, (url) => {
            const result = results.get(url);
            const label = linkTokenLabel(url);
            if (result === 'added' || (result === 'duplicate' && !labelsBefore.has(label))) return linkTokenText(label);
            return result === 'duplicate' ? '' : null;
          })
        : searchInput;
      const allLabels = [
        ...linkLabels,
        ...[...results.entries()].filter(([, r]) => r === 'added').map(([url]) => linkTokenLabel(url)),
      ];
      const ownWords = allLabels.length > 0 ? stripLinkTokens(withPlaceholders, allLabels) : withPlaceholders.trim();
      const textToSend = !ownWords ? '' : linkAdded ? withPlaceholders.replace(/\s+/g, ' ').trim() : withPlaceholders;
      if (!textToSend) {
        if (!linkAdded && !hasAttachments) return;
        setIsAttachMenuOpen(false);
        if (isPhoneLayout && heroAnchored) inputRef.current?.blur();
        setSearchInput('');
        setIsDemo(false);
        setDemoType?.(null);
        setEnterPressed(true);
        onSubmitAttachmentsOnly?.();
        return;
      }
      setIsAttachMenuOpen(false);

      // Set submitting flag immediately
      setIsSubmitting(true);

      // Phones, in-page composer: let go of the keyboard here, in the event
      // handler. The textarea is about to be disabled, and a focus lost that
      // way (inside React's commit) never reaches onBlur on older Chromium,
      // which would leave `isFocused` stale for the rest of the turn.
      if (isPhoneLayout && heroAnchored) inputRef.current?.blur();

      // If topMenu is true OR we're in an active conversation, skip suggestion validation and proceed directly
      if (topMenu) {
        setEnterPressed(true);
        if (textToSend.length > 0) {
          setIsDemo(false);
          setDemoType?.(null);
          submitInput(textToSend);
        }
        return;
      }
      
      // SUGGESTIONS DISABLED - Skip suggestion validation and proceed directly with search
      // If you want to re-enable suggestions, uncomment the PreSearchSuggestionList component
      // and uncomment the validation logic below
      
      // TODO: Still not working very well all the time work on waiting for the suggestion now
      // const suggestions = await waitForValidSuggestion();

      // if (suggestions && suggestions.length > 0) {
      //   const suggestion = suggestions[0];

      //   if (suggestion === '404') {
      //     cancelSearch();
      //     throw new Error('Received 404 error from search propositions');
      //   }
      // } else if (suggestions?.length == 0) {
      //   console.error('[pressSearch] No suggestions found, aborting search');
      //   setEnterPressed(false);
      //   stopSearchProposition();
      //   return;
      // }

      // Continue with normal processing.
      setEnterPressed(true);
      if (textToSend.length > 0) {
        setIsDemo(false);
        setDemoType?.(null);
        // Set input FIRST to trigger search flow - stopSearchProposition/setSearchPropositions
        // can throw when useChat has api:undefined, which would prevent setInput from running
        submitInput(textToSend);
        stopSearchProposition();
        setSearchPropositions([]);
      }
    } catch (error) {
      console.error('[pressSearch] Error occurred:', error);
      alert('Invalid search input or error occurred while searching, try again.');
      setIsSubmitting(false);
    } finally {
      // Reset submitting flag so user can search again (same or different query)
      setIsSubmitting(false);
    }
  };

  // Handle key events in the textarea
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // The caret never sits inside a link placeholder: an arrow at its edge steps over it.
    if (linkLabels.length > 0 && (e.key === 'ArrowLeft' || e.key === 'ArrowRight') && !e.shiftKey && !e.altKey && !e.metaKey && !e.ctrlKey) {
      const el = e.currentTarget;
      const caret = tokenArrow(
        e.key,
        el.selectionStart ?? 0,
        el.selectionEnd ?? 0,
        findLinkTokens(el.value, linkLabels),
      );
      if (caret !== null) {
        e.preventDefault();
        el.setSelectionRange(caret, caret);
        return;
      }
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
      // Allow paste without clearing conversation
      // Only clear search propositions to prepare for new input
      stopSearchProposition();
      setSearchPropositions([]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prevIndex = 0) => (prevIndex < searchPropositions.length ? prevIndex + 1 : prevIndex));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prevIndex = 0) => (prevIndex > 0 ? prevIndex - 1 : 0));
    } else if (e.key === 'Enter' && !e.shiftKey && (searchInput.length > 0 || hasAttachments) && !isSubmitting) {
      setEnterPressed(true);
      e.preventDefault();
      pressSearch(true);
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
    }
  };

  const handleFocusAndShowNote = () => {
    if (inputRef.current) {
      setFocusAttention(true);
      inputRef.current.focus();
      setShowNote(true);
      setTimeout(() => setFocusAttention(false), 1500);
    }
  };

  // Handle minimize/collapse
  const handleMinimizeToggle = () => {
    // Collapse/expand only toggles the panel's visibility. It must NOT repurpose
    // the search bar text — the composer keeps whatever the user had, and the
    // panel is restored by re-focusing the search bar (see the textarea onFocus).
    setIsMinimized((prev) => !prev);
  };

  // Handle textarea input change
  /** The field now reads `value`, as if the shopper typed it (typing and pasted words). */
  const applyTypedValue = (value: string) => {
    // Reset voice input flag when user types manually
    setIsVoiceInput(false);
    // Reset submitting flag when user modifies input
    setIsSubmitting(false);
    // Clear search rejected message when user types
    setSearchRejectedMessage?.(null);
    setSearchPropositionInput(value);
    setSearchInput(value);
    if (value.length === 0) {
      // Don't cancel the entire search when clearing input - user might be typing a follow-up
      // Just stop and clear the search propositions (the suggestion dropdown)
      stopSearchProposition();
      setSearchPropositions([]);
    }
    if (value.length > 0) {
      // Exit demo mode when user starts typing to enable search suggestions
      setIsDemo(false);
      setDemoType?.(null);
      if (!isSearching) {
        setIsSearching(true);
      }
    }
  };

  /** Put the caret back after React rewrote the value (it jumps to the end). */
  const placeCaret = (el: HTMLTextAreaElement, caret: number) => {
    requestAnimationFrame(() => {
      if (document.activeElement !== el) return;
      try {
        el.setSelectionRange(caret, caret);
      } catch {
        // a detached or disabled field: nothing to place
      }
    });
  };

  const handleInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const el = event.target;
    // An edit that cut into a link placeholder takes all of it out, however
    // the text changed (hardware key, soft keyboard, selection typed over).
    const repaired =
      linkLabels.length > 0
        ? repairDamagedTokens(textareaValue, el.value, findLinkTokens(textareaValue, linkLabels))
        : null;
    if (repaired) {
      applyTypedValue(repaired.text);
      placeCaret(el, repaired.caret);
      return;
    }
    applyTypedValue(el.value);
  };

  // A placeholder is one unit: a click or tap inside selects all of it (then
  // it can be dragged, or cut and pasted somewhere else in the sentence); a
  // selection never starts or ends in the middle of one.
  const handleSelect = (event: React.SyntheticEvent<HTMLTextAreaElement>) => {
    if (linkLabels.length === 0) return;
    const el = event.currentTarget;
    const { selectionStart, selectionEnd } = el;
    if (selectionStart === null || selectionEnd === null) return;
    const snapped = snapSelection(selectionStart, selectionEnd, findLinkTokens(el.value, linkLabels));
    if (snapped) el.setSelectionRange(snapped[0], snapped[1], el.selectionDirection ?? 'none');
  };

  /** The shopper takes a link chip away: its placeholder goes from the sentence too. */
  const handleRemoveAttachment = (id: string) => {
    const index = linkItems.findIndex((link) => link.id === id);
    if (index >= 0) {
      const range = findLinkTokens(searchInput, linkLabels).find((r) => r.linkIndex === index);
      if (range) applyTypedValue(removeLinkToken(searchInput, range));
    }
    onRemoveAttachment?.(id);
  };

  // --- Focusing the bar moves nothing on desktop ------------------------------
  // Three separate mechanisms used to move the page the moment the field took
  // focus: a smooth scrollTo(0) on desktop mousedown, a visualViewport tracker
  // that glued the textarea to the middle of the visible area on phones, and a
  // pin-to-top that re-asserted offset 0 for 650ms while the keyboard animated.
  // Layered on the hero's own collapse, they fought each other and the browser's
  // native pan. Desktop stays put. On phones the composer grows in place and
  // the search block is lifted (above) once the visual viewport has actually
  // shrunk; it comes back down when the keyboard's job is done.

  // Help functionality
  const handleQuickSearch = (searchQuery: string) => {
    setSearchInput(searchQuery);
    setInput(searchQuery);
    setShowHelpOptions(false);
    setIsSearching(true);
    // Trigger the search execution
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.blur(); // Trigger the search
      }
    }, 100);
  };

  const handleContactHelp = () => {
    setShowHelpOptions(false);
    // Navigate to contact page
    window.location.href = `/${locale.path}/contact-us`;
  };

  const handleCompareProductsHelp = () => {
    if (!productTitles || productTitles.length < 2) {
      // If no products available, fall back to generic comparison query
      handleQuickSearch(searchContent.helpAssistance.helpCompareProductsQuery);
      return;
    }
    // The SearchHelpPanel will handle the comparison selection internally
    // so we don't need to do anything here - just let the panel handle it
  };

  const handleCompareProducts = (product1: string, product2: string) => {
    const compareQuery = searchContent.helpAssistance.compareTemplate
      .replace('{product1}', product1)
      .replace('{product2}', product2);

    // Set search intent to page-question directly (skip classification)
    setCurrentSearchIntent?.('page-question');

    setSearchInput(compareQuery);
    setInput(compareQuery);
    setShowHelpOptions(false);
    setIsSearching(true);
    setEnterPressed(true);

    // Clear any existing messages to ensure fresh start
    setMessages([]);

    // Use setTimeout to ensure state updates complete
    setTimeout(() => {
      if (inputRef.current) {
        // Focus and blur to trigger search execution
        inputRef.current.focus();
        inputRef.current.blur();
      }
    }, 50);
  };

  const handleSearchHistorySelect = (searchQuery: string) => {
    // Close help panel
    setShowHelpOptions(false);

    // Populate search input with the selected query
    setSearchInput(searchQuery);

    // Focus on the input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  /* -----------------------------------------------------------
   * Image attachment (camera buttons + paste)
   * --------------------------------------------------------- */
  const openImagePicker = (hadFocus: boolean = document.activeElement === inputRef.current) => {
    if (!imageInputRef.current) return;
    // Remember whether the keyboard was up. Focus is handed back after the
    // picker only if it was — otherwise reaching for the camera from a resting
    // bar would open the keyboard nobody asked for and shove the hero upward.
    // The "+" menu passes what it saw when it opened (its own rows took no focus).
    pickerHadFocusRef.current = hadFocus;
    setIsImagePickerOpen(true);
    imageInputRef.current.click();
  };

  const handleImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsImagePickerOpen(false);
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      onAddImage?.(file);
      if (pickerHadFocusRef.current) inputRef.current?.focus();
    }
    // Reset so picking the same file again re-triggers onChange
    event.target.value = '';
  };

  // The native picker takes document focus, blurring the textarea; a chosen
  // file re-focuses via handleImageFileChange, but a CANCELLED picker used to
  // leave the bar collapsed. The input's `cancel` event (Chrome 113+ /
  // Safari 16.4+) settles it, with a delayed window-focus fallback for
  // desktop file dialogs (delayed so a real `change` always wins the race).
  useEffect(() => {
    if (!isImagePickerOpen) return;
    const inputEl = imageInputRef.current;
    const settle = () => {
      // Functional set: only refocus if handleImageFileChange never ran.
      setIsImagePickerOpen((stillOpen) => {
        if (stillOpen && pickerHadFocusRef.current) inputRef.current?.focus();
        return false;
      });
    };
    const onCancel = () => settle();
    const onWindowFocus = () => {
      window.setTimeout(settle, 300);
    };
    inputEl?.addEventListener('cancel', onCancel);
    window.addEventListener('focus', onWindowFocus);
    return () => {
      inputEl?.removeEventListener('cancel', onCancel);
      window.removeEventListener('focus', onWindowFocus);
    };
  }, [isImagePickerOpen, inputRef]);

  /* -----------------------------------------------------------
   * The "+" menu (photo, link, note)
   * --------------------------------------------------------- */
  const canAttach = Boolean(onAddImage || onAddLink || onAddNote);
  // Something to send: words, or attachments alone (a pasted link is a question in itself).
  const canSend = searchInput.length > 0 || hasAttachments;

  const openAttachMenu = (fromKeyboard: boolean) => {
    attachMenuHadFocusRef.current = document.activeElement === inputRef.current;
    setShowHelpOptions(false);
    setAttachMenuFromKeyboard(fromKeyboard);
    setIsAttachMenuOpen(true);
  };

  // Focus goes back to the search field when it had it (or on a desktop,
  // where no keyboard can be summoned by it); a keyboard user who opened the
  // menu from its button lands back on that button.
  const closeAttachMenu = useCallback(
    (opts?: { refocus?: boolean }) => {
      setIsAttachMenuOpen(false);
      if (opts?.refocus === false) return;
      if (attachMenuHadFocusRef.current || (!isPhoneLayout && !attachMenuFromKeyboard)) {
        inputRef.current?.focus({ preventScroll: true });
      } else if (attachMenuFromKeyboard) {
        (attachButtonRef.current ?? buttonRef.current)?.focus({ preventScroll: true });
      }
    },
    [attachMenuFromKeyboard, inputRef, isPhoneLayout],
  );

  const toggleAttachMenu = (fromKeyboard: boolean) => {
    if (isAttachMenuOpen) closeAttachMenu({ refocus: fromKeyboard });
    else openAttachMenu(fromKeyboard);
  };

  // Dictation takes over the footer: the menu must not float over the stop bar.
  useEffect(() => {
    if (isVoiceListening) setIsAttachMenuOpen(false);
  }, [isVoiceListening]);

  /**
   * Paste: a photo becomes the photo chip (as before). Web addresses become
   * link chips and leave a short placeholder where they were pasted, so
   * "something like ‹Garmin Fenix 8›" still reads; a bare link pasted into an
   * empty bar is just the chip (sent alone it is a question in itself). A list
   * or a long text (copied from Notes) becomes a note chip. Ordinary text
   * pastes as usual.
   */
  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const file = Array.from(event.clipboardData?.files ?? []).find((f) => f.type.startsWith('image/'));
    if (file && onAddImage) {
      event.preventDefault();
      onAddImage(file);
      return;
    }
    if (!onAddLink && !onAddNote) return;
    const text = event.clipboardData?.getData('text/plain') || event.clipboardData?.getData('text/uri-list') || '';
    if (!text) return;
    const { urls, note, text: words } = classifyPaste(text);
    if (urls.length === 0 && !note) return; // ordinary typing
    event.preventDefault();
    const el = event.currentTarget;
    const current = el.value;
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? start;

    const results = new Map<string, AddAttachmentResult>();
    for (const url of urls) results.set(url, onAddLink?.(url) ?? 'invalid');
    if (note) onAddNote?.(note);
    if (note) return; // a note's own addresses are chips only: they sit inside text we keep whole

    const inSentence = Boolean(words) || current.slice(0, start).trim() !== '' || current.slice(end).trim() !== '';
    if (!inSentence) return; // a bare link in an empty bar: the chip is all of it
    const placed = new Set(findLinkTokens(current, linkLabels).map((r) => r.label));
    const piece = replaceLinksInText(text, (url) => {
      const result = results.get(url);
      const label = linkTokenLabel(url);
      if (result === 'added' || (result === 'duplicate' && !placed.has(label))) {
        placed.add(label);
        return linkTokenText(label);
      }
      return result === 'duplicate' ? '' : null;
    })
      .replace(/\s+/g, ' ')
      .trim();
    if (!piece) return;
    const edit = insertInlineText(current, start, end, piece);
    applyTypedValue(edit.text);
    placeCaret(el, edit.caret);
  };

  /* -----------------------------------------------------------
   * Suggestion chips: FILL the bar, never run the search
   * --------------------------------------------------------- */
  // Mirrors the typing path so downstream state matches a hand-typed query.
  // CRITICAL: never call setInput / setEnterPressed here — search-bar.tsx
  // auto-runs a search whenever `input` changes, and the whole point of the
  // chips is that a tap costs nothing until the user presses search.
  const handleSuggestionPick = (text: string) => {
    setSearchRejectedMessage?.(null);
    setSearchPropositionInput(text);
    setSearchInput(text);
    setIsDemo(false);
    setDemoType?.(null);
    setIsSearching(true);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.selectionStart = el.selectionEnd = text.length;
      }
    });
  };

  const headerWidth = embedded
    ? 'w-full max-w-none'
    : `lg:max-w-[36rem] md:max-w-[30rem] max-w-md max-md:ml-4`;
  const headerLeftAlign = embedded
    ? 'left-0'
    : 'lg:left-[calc(50%-18rem)] md:left-[calc(50%-16rem)] sm:left-[calc(50%-15rem)]';
  // The same bar edge seen from the right: the "+" menu hangs from it.
  const headerRightAlign = embedded
    ? 'right-0'
    : 'lg:right-[calc(50%-18rem)] md:right-[calc(50%-16rem)] sm:right-[calc(50%-15rem)]';
  const isInSearch = searchInput.length > 0;
  const messageIsNotEmpty = messages.length > 0;
  const showingSuggestions = isSearching && !messages?.length && searchPropositions.length > 0 && !result && !isDoneFinal && !topMenu;

  // Extract the latest assistant message's preamble + question to display above the textarea,
  // plus the AI-chosen per-turn interaction mode that drives the adaptive layout.
  const { currentPreamble, currentQuestionPrompt, latestAssistantId, latestTurnMode, isFirstAssistantTurn, isStandByTurn } = useMemo(() => {
    const empty = {
      currentPreamble: null as string | null,
      currentQuestionPrompt: null as string | null,
      latestAssistantId: undefined as string | undefined,
      latestTurnMode: 'refine' as 'discussion' | 'refine',
      isFirstAssistantTurn: true,
      isStandByTurn: false,
    };
    if (!messages || messages.length === 0) return empty;

    // Find the latest assistant/system message (excluding region-indication which is internal)
    const assistantMessages = messages.filter(
      m => (m.role === 'assistant' || m.role === 'system') && m.id !== 'region-indication'
    );
    if (assistantMessages.length === 0) return empty;

    const latestAssistantMessage = assistantMessages[assistantMessages.length - 1];

    // Streaming fallback for the layout mode: while the newest assistant turn hasn't produced
    // parseable content yet (fresh empty message), or while waiting between turns, reuse the
    // PREVIOUS assistant turn's mode so the discussion layout doesn't flash back to refine at
    // the start of every stream.
    const previousAssistantMessage = assistantMessages[assistantMessages.length - 2];
    const previousTurnMode: 'discussion' | 'refine' = previousAssistantMessage
      ? (parseAssistantMessage({ message: previousAssistantMessage }).turnMode ?? 'refine')
      : 'refine';

    // Between turns: the user has just answered and the next assistant turn hasn't started
    // streaming yet, so `latestAssistantMessage` is still the PREVIOUS question. Suppress its
    // preamble/question so the banner shows only the loading dots — otherwise the just-answered
    // turn's friendly acknowledgment briefly re-flashes before the next turn arrives. We keep
    // latestAssistantId stable so the typewriter only resets when a genuinely new turn streams.
    if (messages[messages.length - 1].role === 'user') {
      const latestParsed = parseAssistantMessage({ message: latestAssistantMessage });
      return {
        currentPreamble: null,
        currentQuestionPrompt: null,
        latestAssistantId: latestAssistantMessage.id,
        latestTurnMode: latestParsed.comparison ? 'discussion' : latestParsed.turnMode ?? previousTurnMode,
        isFirstAssistantTurn: assistantMessages.length <= 1,
        isStandByTurn: false,
      };
    }

    // Parse the message to get the friendly preamble + clarifying question.
    // turnMode is the 2nd JSON field, so when a new turn streams in, "message"
    // parses BEFORE "turnMode" arrives. During that window parsed.turnMode is
    // undefined — keep the PREVIOUS turn's mode so the discussion thread doesn't
    // flash to the refine banner and back as the answer starts streaming.
    const parsed = parseAssistantMessage({ message: latestAssistantMessage });
    const banner = bannerCopyForTurn({
      isDone: parsed.isDone,
      message: parsed.message,
      prompt: parsed.prompt,
      standByMessage: searchContent.standByMessage,
    });
    return {
      currentPreamble: banner.preamble,
      currentQuestionPrompt: banner.question,
      latestAssistantId: latestAssistantMessage.id,
      // A comparison only renders in the thread: a turn carrying one is a
      // discussion turn whatever mode the model wrote.
      latestTurnMode: parsed.comparison ? 'discussion' : parsed.turnMode ?? previousTurnMode,
      isFirstAssistantTurn: assistantMessages.length <= 1,
      isStandByTurn: banner.standBy,
    };
  }, [messages, searchContent.standByMessage]);

  // Discussion layout: conversational thread below the composer instead of banner question +
  // answer chips. Falls back to the classic refine/done rendering the moment isDoneFinal hits
  // so LoadingProducts, the Algolia menu and the price selector behave exactly as today.
  const isDiscussionTurn = latestTurnMode === 'discussion' && !isDoneFinal;

  // Refine-mode answer chips for the latest unanswered turn. They render ABOVE the bar
  // (inside the prompt panel, under the question) — the below-bar area belongs to the
  // live results shelf. Mirrors the gating the old below-bar Messages flow used.
  const refineTurnChoices = useMemo(() => {
    const assistantMessages = messages.filter(
      (m) => (m.role === 'assistant' || m.role === 'system') && m.id !== 'region-indication'
    );
    const latest = assistantMessages[assistantMessages.length - 1];
    if (!latest) return { choices: [] as string[], showSkip: false };
    const latestIdx = messages.indexOf(latest);
    const answered = latestIdx >= 0 && latestIdx < messages.length - 1 && messages[latestIdx + 1].role === 'user';
    const parsed = parseAssistantMessage({ message: latest });
    const hasSuggestions =
      !answered && !parsed.isDone && (parsed.suggestedUserAnswers?.length ?? 0) > 0;
    if (!hasSuggestions) return { choices: [] as string[], showSkip: false };
    // Skip is hidden on an obligatory first question (same rule as before the move).
    const showSkip = !(parsed.obligatory && assistantMessages.length <= 1);
    return { choices: parsed.suggestedUserAnswers, showSkip };
  }, [messages]);

  // Chip clicks feed the same input pipeline as typing (search-bar's input effect).
  // Skip, and a chip whose words equal the last submission (the same answer to
  // two questions in a row), clear first so the effect fires again.
  const handleRefineChipClick = useCallback(
    (choice: string) => {
      if (choice === searchContent.skipInput || choice === input) {
        setInput('');
        setTimeout(() => setInput(choice), 0);
        return;
      }
      setInput(choice);
    },
    [searchContent.skipInput, setInput, input]
  );

  // Scrollback history: the original query + every ANSWERED turn, derived from the
  // same messages array refineTurnChoices reads. The latest unanswered assistant
  // turn is excluded — that's the current question, owned by PromptBanner.
  const pastTurns = useMemo<HistoryTurn[]>(() => {
    if (!messages || messages.length === 0) return [];
    const result: HistoryTurn[] = [];
    // A turn that was only attachments reads as what was attached ("Link: amazon.ca").
    const attachmentLabels = {
      photo: searchContent.attachPhoto,
      link: searchContent.attachLink,
      note: searchContent.attachNote,
    };
    const userText = (msg: Message) => unwrapUserInput(msg.content) || attachmentSummaryLabel(msg, attachmentLabels);
    if (messages[0]?.role === 'user') {
      result.push({
        kind: 'query',
        queryText: userText(messages[0]),
        queryLinks: readMessageAttachments(messages[0]).links,
      });
    }
    for (let i = 0; i < messages.length - 1; i++) {
      const m = messages[i];
      if ((m.role !== 'assistant' && m.role !== 'system') || m.id === 'region-indication') continue;
      const next = messages[i + 1];
      if (!next || next.role !== 'user') continue;
      const parsed = parseAssistantMessage({ message: m });
      if (!parsed.prompt) continue;
      result.push({
        kind: 'answer',
        assistantMessageId: m.id,
        questionText: parsed.prompt,
        suggestedAnswers: parsed.suggestedUserAnswers,
        answerText: userText(next),
        answerLinks: readMessageAttachments(next).links,
      });
    }
    return result;
  }, [messages, searchContent]);

  // True while a history editor is open — suspends the panel's idle auto-collapse
  // so it can't snap away mid-edit.
  const [isHistoryEditingActive, setIsHistoryEditingActive] = useState(false);

  // A resumed turn was already read before the refresh — it renders fully-formed
  // (no typewriter replay) and counts as "already complete" for the chip gate.
  const isRestoredTurn = !!latestAssistantId && !!restoredTurnIds?.has(latestAssistantId);

  // Per-turn typewriter completion gate. Reset whenever the latest assistant message
  // changes (i.e. a new turn started streaming). Set true when PromptBanner finishes
  // typing the question, which then reveals the suggestion chips below the bar.
  // Restored turns start already-true: PromptBanner's instant path fires its
  // completion callback from a CHILD effect, which React commits BEFORE this parent
  // effect — resetting to false unconditionally would swallow it and hide the chips.
  const [questionTypewriterDone, setQuestionTypewriterDone] = useState(false);
  const lastAssistantIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (latestAssistantId !== lastAssistantIdRef.current) {
      lastAssistantIdRef.current = latestAssistantId;
      setQuestionTypewriterDone(isRestoredTurn);
    }
  }, [latestAssistantId, isRestoredTurn]);
  const handleQuestionTypewriterComplete = useCallback(() => {
    setQuestionTypewriterDone(true);
  }, []);

  // Keep container visible during the transition from region selector to product search
  // This covers the gap between region skip and when messages start arriving
  // EXPANDED: Also keep visible when intent is product-search and we're searching (even if not loading yet)
  // REMOVED the !messageIsNotEmpty condition - it was preventing container from showing when we already had messages
  // Suppress product-search loading UI while we're silently checking Algolia for a redirect.
  // This prevents a loading flash when the user is about to be redirected to an existing page.
  const isProductSearchLoading = (isLoading || isSearching) && currentSearchIntent === 'product-search' && !isDoneFinal && !isCheckingAlgoliaForRedirect;

  // `isMinimized` must win over every "keep it open" signal (loading, discussion,
  // completed results) — otherwise the minimize button flips the flag but the panel
  // stays rendered. Re-focusing the search bar clears isMinimized and brings it back.
  const containerVisible = !isMinimized && (isProductSearchStarted || showRegionSelector || isProductSearchLoading || isProductBranchLoading || (isDoneFinal && (algoliaMatchs || currentSearchIntent === 'product-search')));
  // Displayable matches for the live results shelf (below the bar).
  const shelfHits: AlgoliaHit[] = useMemo(
    () => (!algoliaMatchs ? [] : Array.isArray(algoliaMatchs) ? algoliaMatchs : [algoliaMatchs]),
    [algoliaMatchs],
  );
  const hasShelfHits = shelfHits.length > 0;
  // The products the conversation is about, as the focus shelf found them in
  // the catalogue: the comparison card's photos, prices and links.
  const focusVisuals = useMemo(() => focusVisualsFromHits(shelfHits, locale), [shelfHits, locale]);
  const showLoadingProducts =
    (isDoneFinal && !hasShelfHits) ||
    (isProductBranchLoading && currentSearchIntent === 'product-search');
  // The budget chip steps aside while results load.
  const hideBudgetChip = showLoadingProducts;
  // A restored completed search briefly has isDoneFinal=true with no matches yet
  // (they re-fetch on mount). Without this guard the page-CREATION theater would
  // flash for a search that finished long ago.
  const showCreationTheater = showLoadingProducts && !isRestoredTurn && !hasShelfHits;
  const composerDisabled =
    (isDoneFinal && !algoliaMatchs && !isRestoredTurn) || isLoading || isClassifyingIntent || isProductBranchLoading;

  // Stop hands the words back. On a desktop the caret goes back to the end of
  // them, once the field is enabled again; a phone is not handed a keyboard it
  // did not ask for.
  const refocusAfterStopRef = useRef(false);
  // Stop sits where Send was, and after it the bar's own button (Send, or the
  // X that clears the whole search) is back in that very spot: the second
  // click of a double-click on Stop must not land on it.
  const stopTappedAtRef = useRef(-Infinity);
  const isStopEcho = () => performance.now() - stopTappedAtRef.current < STOP_ECHO_MS;
  const handleStopSearch = () => {
    stopTappedAtRef.current = performance.now();
    refocusAfterStopRef.current = !isPhoneLayout;
    stopSearch();
  };
  useEffect(() => {
    const el = inputRef.current;
    if (!refocusAfterStopRef.current || composerDisabled || !el) return;
    refocusAfterStopRef.current = false;
    el.focus({ preventScroll: true });
    el.setSelectionRange(el.value.length, el.value.length);
  }, [composerDisabled, inputRef]);

  /* -----------------------------------------------------------
   * Card top region — what the capsule shows ABOVE the composer
   * --------------------------------------------------------- */
  // The Q&A banner, the discussion thread and the region map
  // used to float above the bar as separate glass panels. They are now the top
  // region of the search card itself (see CardTopRegion inside the capsule):
  // one bordered surface, composer at the bottom, conversation on top. These
  // gates are unchanged from the floating days — only the placement moved.
  const cardTopEligible = !topMenu || isMobileView;
  const productConversationVisible =
    messageIsNotEmpty &&
    !isMinimized &&
    !isSwapped &&
    !showRegionSelector &&
    currentSearchIntent === 'product-search' &&
    cardTopEligible;
  const showThreadPanel = productConversationVisible && isDiscussionTurn && !productSearchParseError;
  // A RESTORED concluded search hides the banner: its last line is the AI's
  // "stand by, searching now…" hand-off, which is stale by the time the user
  // comes back — and the shelf's "here's what I found" already says where we
  // landed. Live conclusions keep it (the line just appeared), except while
  // the page is built: its "stand by" would only repeat the loading row.
  const showBannerPanel =
    productConversationVisible &&
    (!isDiscussionTurn || !!productSearchParseError) &&
    !(isDoneFinal && isRestoredTurn) &&
    !showCreationTheater;
  const showRegionPanel = showRegionSelector && cardTopEligible && !!onRegionConfirm && !!onRegionSkip;
  const showBrowsingPill = !!browsingLabel && !isDoneFinal && cardTopEligible;
  const cardTopActive = showThreadPanel || showBannerPanel || showRegionPanel || showBrowsingPill;

  // Layout switch for the two-row capsule. Deliberately distinct from
  // isProductSearchStarted, which stays the semantic "a product conversation
  // exists" flag used for panels, headings and intent checks. Any card-top
  // content forces the surfaced capsule too — a region takeover before
  // the first message would otherwise land on the transparent classic pill.
  // An attachment (photo, link, note) also brings the card: its chips live in
  // the composer footer, next to the "+" that added them.
  const isComposerLayout = isProductSearchStarted || focusComposerOpen || cardTopActive || hasAttachments;

  // The scale-in flourish class is applied for as long as a conversation OR a
  // card-top panel exists (a stable class only animates when it first lands),
  // so the first message does not re-pop a card the region panel already
  // opened, and a phone focus-morph (focusComposerOpen alone) stays flourish-free.
  const composerFlourish = isProductSearchStarted || cardTopActive;

  // Hero page only: the card grows UPWARD. The hero copy above the bar stays in
  // flow (it is the natural spacer), and the capsule pulls its own top edge up by
  // exactly the card-top's height (`--card-top-h`, written imperatively from the
  // region's ResizeObserver so it lands in the same frame as the layout change,
  // never a render behind). Net effect: the composer never moves; the region
  // overhangs the (now invisible) title/description. (On a phone's discussion
  // thread the pull stops at the slot and the card grows DOWN from there, the
  // composer settling near the bottom of the screen: see phone-thread-fit.ts.)
  // The clamp `--hero-slot-max`
  // is the space between the top of #main-search-bar (right under the sticky nav
  // at scroll 0) and the composer, minus a gap — same 12px the floating panels
  // kept from the nav — so the card can never run under the header.
  useEffect(() => {
    if (!heroAnchored) return;
    const capsule = capsuleRef.current;
    const row = barRowRef.current;
    const hero = document.getElementById('main-search-bar');
    if (!capsule || !row || !hero) return;
    const measure = () => {
      // Rects, not offsetTop: the row and hero have unrelated offset parents.
      // The row's top is invariant to the card (its margin box shrinks by the
      // exact overhang), so this never feeds back into itself.
      const rowTop = row.getBoundingClientRect().top;
      const heroTop = hero.getBoundingClientRect().top;
      capsule.style.setProperty('--hero-slot-max', `${heroSlotMax(rowTop, heroTop)}px`);
    };
    measure();
    // Observe the hero COPY, never `#main-search-bar` itself. The gap measured
    // above reduces to the hero's own padding plus the copy's height — the
    // card's overhang cancels out of `rowTop - heroTop`, which is the invariance
    // noted in `measure` — so the hero's height is not an input here. It is an
    // OUTPUT: `--card-top-h`, which CardTopRegion's own ResizeObserver writes on
    // every frame the card-top grows or shrinks, shortens the hero by exactly
    // that much. Observing it meant that write resized an observed element five
    // levels SHALLOWER than the one being broadcast, which the ResizeObserver
    // algorithm calls a skipped observation: the browser drops the notification
    // to the next frame and logs "ResizeObserver loop completed with undelivered
    // notifications" — several times per search on the live site, and the card's
    // margin correction landed a frame late every time it happened. The viewport
    // reaches the hero padding (`50svh`) through `resize`; nothing else moves the
    // gap, since a banner appearing above the hero shifts `rowTop` and `heroTop`
    // together.
    const copy = document.getElementById('search-content-container');
    const ro = new ResizeObserver(measure);
    if (copy) ro.observe(copy);
    window.addEventListener('resize', measure, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [heroAnchored]);

  /* -----------------------------------------------------------
   * Effect: Autosize & Initialize Input
   * --------------------------------------------------------- */
  useEffect(() => {
    const inputEl = inputRef.current;
    if (!inputEl) return;

    // 1) autosize the input (if it's a textarea; if it's an <input>, use a hidden <textarea> for size or skip this)
    autosize(inputEl);
    // autosize() is a no-op on an element it already manages, so re-measure explicitly:
    // the value can change without a keystroke (AI-rewritten query, restored turn).
    autosize.update(inputEl);

    // 2) force the button's height to match the input's computed height
    // In composer mode (product search started) the button and voice control are in
    // normal flow inside the capsule footer, so inline geometry must be cleared instead.
    const syncButtonHeight = () => {
      // Batch all geometric property reads in requestAnimationFrame to avoid forced reflow
      requestAnimationFrame(() => {
        const buttonEl = buttonRef.current;

        // Use scrollHeight to get the actual content height without margins
        const actualHeight = inputEl.scrollHeight;
        const windowWidth = window.innerWidth;

        // Batch all writes after reads - only if button exists
        if (buttonEl) {
          buttonEl.style.height = isComposerLayout ? '' : `${actualHeight - 2}px`;
        }

        // 3) add margin-top for header to prevent expansion above page top
        if (header) {
          const threshold = windowWidth >= 2049 ? 52 : 41;
          const marginTop = isComposerLayout ? 0 : Math.max(0, actualHeight - threshold);
          inputEl.style.marginTop = `${marginTop}px`;
          if (buttonEl) {
            buttonEl.style.marginTop = `${marginTop}px`;
          }

          // 4) apply half margin-top to voice/speech buttons to keep them centered
          if (voiceButtonContainerRef.current) {
            const halfMarginTop = marginTop / 2;
            voiceButtonContainerRef.current.style.marginTop = `${halfMarginTop}px`;
          }
          if (speechToTextRef.current) {
            const halfMarginTop = marginTop / 2;
            speechToTextRef.current.style.marginTop = `${halfMarginTop}px`;
          }
        }
      });
    };

    // call once right away (in case the input was just resized)
    syncButtonHeight();

    // also re-sync whenever the window resizes (optional, but can catch font/zoom changes)
    window.addEventListener('resize', syncButtonHeight);

    // cleanup on unmount or deps‐change
    return () => {
      window.removeEventListener('resize', syncButtonHeight);
      autosize.destroy(inputEl);
    };
  }, [messages, textareaValue, header, inputRef, isComposerLayout]);

  // Kit-scope state for the shelf's assembling-kit tray: the AI's per-turn scope
  // decision plus the cumulative user-confirmed elements (a turn that omits the
  // field keeps the last non-empty set — mirrors search-bar's ref semantics).
  // `openingKitElements`: what the AI already called confirmed before the
  // shopper's first answer, which in a seeded conversation is the seed itself.
  const { latestSearchScope, confirmedKitElements, openingKitElements } = useMemo(() => {
    let scope: 'single' | 'kit' = 'single';
    let elements: string[] = [];
    let opening: string[] = [];
    let userTurns = 0;
    for (const m of messages) {
      if (m.role === 'user') userTurns += 1;
      if ((m.role !== 'assistant' && m.role !== 'system') || m.id === 'region-indication') continue;
      const parsed = parseAssistantMessage({ message: m });
      if (parsed.searchScope) scope = parsed.searchScope;
      if (parsed.confirmedElements.length > 0) {
        elements = parsed.confirmedElements;
        if (userTurns <= 1) opening = parsed.confirmedElements;
      }
    }
    return { latestSearchScope: scope, confirmedKitElements: elements, openingKitElements: opening };
  }, [messages]);
  const kitShelfMode = latestSearchScope === 'kit' && currentSearchIntent === 'product-search';
  const kitTraySeed = seedProduct ? { ...seedProduct, aliases: openingKitElements } : null;
  // Same list the shelf builds, so the gate and the tray never disagree: a
  // seeded conversation has a kit the moment it opens, even before the AI
  // echoes any confirmed topic.
  const kitTrayActive = kitShelfMode && buildKitTrayEntries(confirmedKitElements, kitTraySeed).length > 0;

  // The shelf mounts for page matches OR for the kit tray — but steps aside while
  // the page-creation theater runs (matches cleared + loading state). Keyed off
  // showCreationTheater (not showLoadingProducts) so a restored, concluded kit
  // conversation gets its tray back immediately instead of showing nothing.
  const shelfVisible = hasShelfHits || (kitTrayActive && !showCreationTheater);

  // Whether THIS conversation has put picks on the shelf yet — keyed on the
  // shopper's opening message, so a new search (or an edited opening query)
  // starts over without every reset path having to remember it.
  const conversationKey = useMemo(() => messages.find((m) => m.role === 'user')?.id ?? null, [messages]);
  const [shelfShownIn, setShelfShownIn] = useState<string | null>(null);
  useEffect(() => {
    if (hasShelfHits && conversationKey) setShelfShownIn(conversationKey);
  }, [hasShelfHits, conversationKey]);
  const shelfShownThisConversation = !!conversationKey && shelfShownIn === conversationKey;
  const productConversationOngoing =
    messages.length > 0 && !isMinimized && currentSearchIntent === 'product-search';

  // An answer ruled out every page the shelf was showing and the conversation
  // carries on: the slot says so, and says what happens next, instead of
  // silently vanishing. Only after picks were on screen — a first turn that
  // never had any keeps the quiet hero. A concluding turn that finds nothing
  // is the creation theater's job, not this.
  const showShelfEmptied =
    shelfSettledEmpty &&
    shelfShownThisConversation &&
    productConversationOngoing &&
    !shelfVisible &&
    !isDoneFinal &&
    !showCreationTheater &&
    !productSearchParseError &&
    !showRegionSelector;

  /* -----------------------------------------------------------
   * Effect: Bring the conversation back once the shelf is gone
   * --------------------------------------------------------- */
  // A shopper scrolled down to the picks keeps answering from the chips; when
  // an answer empties the shelf, the page shrinks under them and they are left
  // looking at the space below the bar with the question out of sight above.
  // Return to the composition the search opened with (conversation above the
  // composer, the shelf-emptied note or the creation progress below). Fires on
  // the render the shelf goes away and only then, only upward, only while the
  // conversation carries on (a reset or a new search is not this), and only
  // for the in-page composer — the header dropdown scrolls inside its own
  // panel. A phone with its keyboard up is left to the centering policy above.
  const shelfWasVisibleRef = useRef(false);
  const stopConversationGlideRef = useRef<(() => void) | null>(null);
  const peekScrollTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(
    () => () => {
      stopConversationGlideRef.current?.();
      clearTimeout(peekScrollTimerRef.current);
    },
    [],
  );

  // The "Might interest you" bar at the foot of the card: take the page down to
  // the picks. The composer is blurred first; with the keyboard up the block is
  // still lifted and the browser may be mid-scroll, so the move waits for the
  // lift to come down (the glide reads the page once, at rest, then only writes).
  const scrollToShelf = useCallback(
    (shelf: HTMLElement) => {
      const viewport = window.visualViewport;
      const keyboardWasUp = !!viewport && isKeyboardOpen(window.innerHeight, viewport.height);
      inputRef.current?.blur();
      const go = () => {
        const block = capsuleRef.current?.closest<HTMLElement>('#main-search-bar');
        const target = shelfScrollTarget({
          scrollY: window.scrollY,
          shelfTop: shelf.getBoundingClientRect().top,
          blockShift: block ? new DOMMatrixReadOnly(getComputedStyle(block).transform).m42 : 0,
          headerBand: parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0,
        });
        stopConversationGlideRef.current?.();
        stopConversationGlideRef.current = glideWindowScrollTo(target);
      };
      clearTimeout(peekScrollTimerRef.current);
      if (keyboardWasUp) peekScrollTimerRef.current = setTimeout(go, KEYBOARD_LIFT_MS + 80);
      else go();
    },
    [inputRef],
  );
  useEffect(() => {
    const wasVisible = shelfWasVisibleRef.current;
    shelfWasVisibleRef.current = shelfVisible;
    if (!wasVisible || shelfVisible) return;
    if (!productConversationOngoing || topMenu || header) return;
    const capsule = capsuleRef.current;
    if (!capsule) return;
    const viewport = window.visualViewport;
    if (isPhoneLayout && viewport && isKeyboardOpen(window.innerHeight, viewport.height)) return;
    const block = capsule.closest('#main-search-bar') ?? capsule;
    const target = conversationReturnScrollTop({
      scrollY: window.scrollY,
      blockTop: Math.min(block.getBoundingClientRect().top, capsule.getBoundingClientRect().top),
      scrollPaddingTop: parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0,
    });
    if (target === null) return;
    stopConversationGlideRef.current?.();
    stopConversationGlideRef.current = glideWindowScrollTo(target);
  }, [shelfVisible, productConversationOngoing, topMenu, header, isPhoneLayout]);

  // Below-bar conversation panel: the topMenu desktop dropdown always hosts the
  // conversation in-panel, and so do the non-product intents (page-question /
  // comparison / recommendation) in every layout — their answers render via
  // Messages, not the product-search banner above the bar.
  const useInPanelConversation =
    (topMenu && !isMobileView) || (!!currentSearchIntent && currentSearchIntent !== 'product-search');

  // Voice search button is rendered in one of two spots: floating at the left of the
  // classic single-row bar ('sm'), or in-flow inside the composer footer ('lg', 44px).
  const renderVoiceSearchButton = (size: 'sm' | 'lg') => (
    <VoiceSearchButton
      size={size}
      controlRef={voiceControlRef}
      onTranscriptUpdate={(text) => {
        setIsVoiceInput(true);
        setSearchInput(text);
        setSearchPropositionInput(text);
      }}
      onInterimTranscript={(text) => {
        // Words appear in the field as they're spoken. Display-only: the
        // proposition input stays untouched (isVoiceInput already gates the
        // propositions debounce), and Whisper still delivers the final text.
        setIsVoiceInput(true);
        setLiveTranscript(text);
        setSearchInput(text);
      }}
      onProcessingChange={setIsSpeechProcessing}
      onListeningChange={(isListening) => {
        setIsVoiceListening(isListening);
        if (!isListening) {
          // Reset voice input flag when voice listening stops — unless live
          // words are pending Whisper: clearing the flag then would let the
          // propositions debounce treat the interim text as typed input.
          if (!liveTranscript) {
            setIsVoiceInput(false);
          }
          setLiveTranscript('');
        }
      }}
      onAudioStreamChange={setAudioStream}
      onStart={() => {
        // Clear existing text immediately when starting voice recording
        setIsVoiceInput(true);
        setLiveTranscript('');
        setSearchInput('');
        setSearchPropositionInput('');

        // Uncollapse menu if minimized and there's an active search
        if (isMinimized && messages.length > 0) {
          setIsMinimized(false);
        }
      }}
      onTranscriptionComplete={() => {
        // Auto-trigger search after voice transcription
        console.log('🎤 Voice transcription complete - auto-starting search');
        pressSearch();
      }}
      isSearching={isSearching}
      disabled={isDoneFinal}
      inputRef={inputRef}
      locale={locale}
    />
  );

  // The one message that must be impossible to miss while the mic is live.
  const voiceStopLabel = isMobile ? searchContent.voiceTapToStop : searchContent.voiceClickToStop;

  // While the mic is live (or its transcription is finishing) the composer
  // footer drops every control — camera, pills, history, send — and becomes a
  // single full-width stop/processing bar.
  const voiceComposerTakeover = isVoiceListening || isSpeechProcessing;

  // Shared by the composer footer (full-width) and the classic floating pill.
  const renderVoiceStopControl = (shape: 'bar' | 'pill') =>
    isVoiceListening ? (
      <button
        type="button"
        // Same guard as every other bar control: without it the mousedown
        // blurs the textarea and the phone composer collapses under the tap.
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => voiceControlRef.current?.stop()}
        aria-label={voiceStopLabel}
        name="voice-stop-button"
        className={`flex h-12 items-center justify-center gap-3 bg-red-500 text-white shadow-lg shadow-red-500/30 transition-colors duration-150 hover:bg-red-600 active:bg-red-700 ${
          shape === 'bar' ? 'w-full min-w-0 rounded-xl animate-fade-in' : 'rounded-full px-6'
        }`}
      >
        <VoiceEqBars />
        <span className="min-w-0 truncate text-base font-semibold tracking-wide">{voiceStopLabel}</span>
        <MdStop className="h-6 w-6 flex-shrink-0" />
      </button>
    ) : (
      <div
        className={`flex h-12 items-center justify-center gap-2 bg-neutral-100 text-zinc-500 dark:bg-white/10 dark:text-zinc-300 ${
          shape === 'bar' ? 'w-full min-w-0 rounded-xl animate-fade-in' : 'rounded-full px-6'
        }`}
      >
        {speechProcessingDots}
        <span className="truncate text-sm font-medium">{searchContent.processing}</span>
      </div>
    );

  const speechProcessingDots = (
    <div className="flex items-center gap-1 px-2 py-1 rounded-md">
      <div className="flex items-center gap-0.5">
        <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-cornflowerblue/70" style={{ animationDelay: '0ms' }} />
        <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-cornflowerblue/70" style={{ animationDelay: '150ms' }} />
        <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-cornflowerblue/70" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  );

  // Composer mode: the capsule owns border/bg/shadow, the textarea goes transparent.
  // Classic mode: the textarea carries the full bar styling (unchanged behavior).
  const ghostPlaceholderClasses = `ghost-placeholder ${ghostFadingOut ? 'ghost-placeholder-out' : ''}`;
  // Dictation text treatment: no words yet → transparent (the centered wave
  // owns the empty row); words streaming → visible, caret hidden (the user is
  // speaking, not editing).
  const voiceTextClasses = isVoiceListening
    ? liveTranscript
      ? 'caret-transparent'
      : 'text-transparent caret-transparent'
    : '';
  const textareaClassName = isComposerLayout
    ? `flex h-[2.7rem] w-full resize-none overflow-y-hidden rounded-2xl bg-transparent px-4 py-1.5 leading-8 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:text-white dark:placeholder-zinc-400 placeholder:overflow-hidden placeholder:text-ellipsis placeholder:whitespace-nowrap max-sm:h-[2.4rem] max-sm:text-[16px] max-xs:py-1 sm:leading-7 ${ghostPlaceholderClasses} ${voiceTextClasses}`
    : `flex h-[2.7rem] w-full resize-none overflow-y-hidden border py-1.5 pl-12 leading-8 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:border-transparent-dark dark:text-white dark:placeholder-zinc-400 placeholder:overflow-hidden placeholder:text-ellipsis placeholder:whitespace-nowrap placeholder:text-center max-sm:h-[2.4rem] max-sm:text-[16px] max-xs:py-1 sm:leading-7 ${ghostPlaceholderClasses} ${showingSuggestions ? 'rounded-md' : 'rounded-full'} ${
        isSearching && !showingSuggestions
          ? `border-input border bg-canvas shadow-search-bar dark:bg-dark dark:shadow-search-bar-dark rounded-md`
          : ''
      } ${enterPressed || (isDoneFinal && !algoliaMatchs) ? 'pr-10' : 'pr-11'} ${
        topMenu ? 'dark:bg-dark/95' : isFocused ? 'dark:bg-dark/90' : 'dark:bg-dark/65'
      } ${voiceTextClasses}`;

  // Same gate the resume chip renders under (kept in sync below) — debounced
  // so blurring the bar doesn't make it pop in immediately: it hides the
  // instant any condition fails, but only shows after RESUME_CHIP_DELAY_MS
  // of every condition holding continuously.
  const canShowResumeChip = Boolean(
    resumeCandidate &&
      !isResumeDismissed &&
      !isFocused &&
      !isSearching &&
      !isVoiceListening &&
      !isProductSearchStarted &&
      !messageIsNotEmpty &&
      !searchInput &&
      !showRegionSelector &&
      !topMenu,
  );
  useEffect(() => {
    if (!canShowResumeChip) {
      setShowResumeChip(false);
      return;
    }
    const timer = setTimeout(() => setShowResumeChip(true), RESUME_CHIP_DELAY_MS);
    return () => clearTimeout(timer);
  }, [canShowResumeChip]);

  return (
    <>
      {/* Hidden file input backing every "add an image" affordance (help menu,
          composer button, paste). accept=image/* lets mobile offer camera or gallery. */}
      {onAddImage && (
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageFileChange}
          className="hidden"
          aria-hidden="true"
          tabIndex={-1}
        />
      )}

      <div
        className={`z-40 items-center transition ${
          header
            ? embedded
              ? 'relative w-full max-sm:hidden'
              : 'fixed inset-x-0 top-3 m-0 justify-center max-sm:hidden'
            : 'relative m-auto w-auto max-w-xl max-sm:max-w-xl xl:max-w-[40rem]'
        }`}
      >
        {!compactMode && topMenu && !isMobileView && (!isProductSearchStarted || currentSearchIntent !== 'product-search') && !showRegionSelector && (
        <h1
        className="mx-auto animate-typing overflow-hidden whitespace-nowrap border-r-2 border-r-current text-center font-mono tracking-tighter dark:border-r-white max-md:tracking-[-0.1em] max-sm:pb-0.5 max-sm:text-[1.75rem] sm:pb-3 sm:text-4xl absolute left-0 right-0 top-6 z-10 sm:hidden"
        translate="no"
      >
        {ui.title}

      </h1>
        )}
        <div
          ref={barRowRef}
          className={`relative flex w-full items-center rounded-full pl-0 overflow-visible -mt-1.5 ${
            topMenu && isMobileView ? 'flex-col' : ''
          } ${header ? `${headerWidth} sm:mx-auto md:mx-auto` : ''} ${focusAttention ? 'animate-search-bar-focus' : ''}`}
        >
          {/* Speech to Text Button - floats at the left of the classic single-row bar. */}
          {/* In composer mode it lives in the capsule footer instead. */}
          {!isComposerLayout && (
            <div
              ref={speechToTextRef}
              className="absolute left-2 top-1/2 z-[100] transform -translate-y-1/2"
              onMouseDown={(e) => e.preventDefault()}
            >
              {renderVoiceSearchButton('sm')}
            </div>
          )}
          
          {/* ── "Pick up where you left off" chip → merges into the full history panel ── */}
          {/* A refresh mid-conversation always brings the chip back — except once the user
              has closed it with the X: that dismissal is persisted on the transcript and the
              chip is never proposed again for that conversation. Focusing the bar hides it.
              Appearance is debounced by RESUME_CHIP_DELAY_MS — see canShowResumeChip /
              showResumeChip above — so it doesn't pop in the instant the bar blurs;
              disappearance stays instant. resume-history-chip.tsx owns the merge-into-panel
              interaction itself: the small clock opens it, the Back button / outside click /
              Escape close it — the main pill click still just resumes this one chat. */}
          {showResumeChip && resumeCandidate && (
            <div className="absolute left-0 right-0 top-full mt-2 z-10 flex justify-center animate-fade-in-from-above">
              <ResumeHistoryChip
                resumeCandidate={resumeCandidate}
                locale={locale}
                backLabel={searchContent.helpAssistance.historyBack ?? 'Back'}
                clearHistoryLabel={searchContent.helpAssistance.clearHistory}
                completedLabel={searchContent.helpAssistance.chatCompleted}
                emptyLabel={searchContent.helpAssistance.noSearchHistory}
                searchPlaceholder={searchContent.helpAssistance.searchHistory}
                onResumeMain={() => {
                  setIsResumeDismissed(true);
                  void onResumeChat?.({
                    transcriptId: resumeCandidate.transcriptId,
                    turnIndex: resumeCandidate.turnIndex,
                  });
                }}
                onResumeOther={(transcriptId, turnIndex) => {
                  void onResumeChat?.({ transcriptId, turnIndex });
                }}
                onDismiss={() => {
                  setIsResumeDismissed(true);
                  dismissChatTranscriptResume(resumeCandidate.transcriptId);
                }}
                onSelectSearch={handleSearchHistorySelect}
              />
            </div>
          )}

          {/* ── Search card ─────────────────────────────────────────────────────────── */}
          {/* Classic: single-row rounded-full bar (textarea owns the styling).          */}
          {/* Composer (product-search Q&A): one card — the conversation region on top   */}
          {/* (question + chips / thread / region map), then the textarea */}
          {/* row and the footer (filter chips bottom-left, mic + send bottom-right).    */}
          {/* On the hero page the card grows UPWARD (negative margin = card-top height, */}
          {/* see `--card-top-h`); the composer row never moves.                         */}
          <div
            ref={capsuleRef}
            // The element that draws the pill gets the Ask chip's pulse (ask-bar-handoff.ts):
            // this card in composer layout, the textarea in the classic bar.
            data-ask-target={isComposerLayout ? '' : undefined}
            onPointerDown={(event) => {
              // The whole pill is the focus target — footer chrome, padding,
              // empty capsule. Controls keep their own preventDefault so a
              // mid-tap button still lands. preventScroll: the viewport
              // policy above is the only thing allowed to move the page.
              // The conversation region is for reading/scrolling, not for
              // summoning the keyboard.
              const target = event.target as HTMLElement | null;
              if (target?.closest('button, a, input, [data-no-focus], [data-search-card-top]')) return;
              inputRef.current?.focus({ preventScroll: true });
            }}
            className={
              isComposerLayout
                ? `relative flex w-full flex-col rounded-2xl border border-input bg-canvas shadow-search-bar transition-[border-radius,background-color,box-shadow] duration-200 ease-out dark:border-transparent-dark dark:bg-dark dark:shadow-search-bar-dark ${
                    composerFlourish ? 'animate-fade-in-scale' : ''
                  } ${topMenu ? 'dark:bg-dark/95' : isFocused || cardTopActive ? 'dark:bg-dark/90' : 'dark:bg-dark/65'} ${
                    heroAnchored ? 'mt-[calc(-1*var(--card-top-h,0px))]' : ''
                  }`
                : 'flex w-full items-center rounded-full border border-transparent transition-[border-radius,background-color,box-shadow] duration-200 ease-out'
            }
          >
            {/* Conversation region — the top of the card. Mounted whenever the
                capsule is the composer so its ResizeObserver keeps `--card-top-h`
                honest (0 when empty). */}
            {isComposerLayout && (
              <CardTopRegion onHeightChange={heroAnchored ? handleCardTopHeight : undefined}>
                {cardTopActive ? (
                <>
                {showThreadPanel ? (
                  <DiscussionThread
                    messages={messages}
                    isLoading={isLoading}
                    locale={locale}
                    setInput={setInput}
                    // The tray below names the same product once it is up —
                    // this row would then say it twice.
                    pinnedProduct={kitTrayActive ? undefined : (seedProduct ?? undefined)}
                    restoredTurnIds={restoredTurnIds}
                    panel="card"
                    sheet={!!topMenu && isMobileView}
                    focusVisuals={focusVisuals}
                    onNavigate={onShelfNavigate}
                  />
                ) : showBannerPanel ? (
                  <AboveBarPromptPanel
                    key={productSearchParseError ? 'error' : 'banner'}
                    placement="card"
                    autoCollapse
                    suspendAutoCollapse={isHistoryEditingActive}
                  >
                    {!productSearchParseError && pastTurns.length > 0 && onEditHistoryTurn && (
                      <AboveBarHistory
                        turns={pastTurns}
                        searchContent={searchContent}
                        isLoading={isLoading}
                        onConfirmEdit={onEditHistoryTurn}
                        onEditingActiveChange={setIsHistoryEditingActive}
                      />
                    )}
                    <PromptBanner
                      variant="above"
                      preamble={currentPreamble}
                      question={currentQuestionPrompt}
                      isLoading={isLoading || isClassifyingIntent}
                      parseError={productSearchParseError}
                      turnId={latestAssistantId ?? 'pending'}
                      isFirstTurn={isFirstAssistantTurn}
                      instant={isRestoredTurn || isStandByTurn}
                      onQuestionTypewriterComplete={handleQuestionTypewriterComplete}
                      errorSlot={(
                        <div className="flex flex-col items-center gap-3 max-sm:pt-5">
                          {/* What failed, as the bar knows it: a refusal says the
                              search could not run; anything else, the generic
                              line. One sentence for both read as a lie (2026-10-13). */}
                          <p className="m-auto max-w-[75%] text-center text-sm leading-snug text-gray-400 dark:text-gray-500">
                            {searchRejectedMessage ?? searchContent.parseErrorRetry}
                          </p>
                          {onProductSearchRetry && (
                            <button
                              type="button"
                              onClick={onProductSearchRetry}
                              disabled={retryWaitSeconds > 0}
                              className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 dark:border-gray-700 bg-transparent px-4 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-gray-200 transition-all duration-150 disabled:pointer-events-none disabled:opacity-50"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                <path d="M3 3v5h5" />
                              </svg>
                              {retryLabel(searchContent.retry, retryWaitSeconds)}
                            </button>
                          )}
                        </div>
                      )}
                    />

                    {/* Answer chips live with the question, in the card's top region —
                        the space below the bar belongs to the live results shelf.
                        Same submit pipeline as typing. */}
                    {!productSearchParseError &&
                      !isLoading &&
                      !isClassifyingIntent &&
                      questionTypewriterDone !== false &&
                      refineTurnChoices.choices.length > 0 && (
                        <div className="mt-3 flex flex-wrap justify-center gap-2 max-sm:gap-1.5">
                          {refineTurnChoices.choices.map((choice) => (
                            <ButtonSuggestion
                              key={choice}
                              choice={choice}
                              isLoading={isLoading}
                              handleSuggestionClick={handleRefineChipClick}
                            />
                          ))}
                          {refineTurnChoices.showSkip && (
                            <ButtonSuggestion
                              choice={searchContent.skipInput}
                              isLoading={isLoading}
                              handleSuggestionClick={handleRefineChipClick}
                            >
                              {searchContent.skip}
                            </ButtonSuggestion>
                          )}
                        </div>
                      )}
                  </AboveBarPromptPanel>
                ) : showRegionPanel ? (
                  <AboveBarPromptPanel placement="card">
                    <p className="m-auto max-w-[80%] overflow-hidden pb-3 text-center text-lg text-gray-600 dark:text-white max-sm:leading-tight">
                      {searchContent.regionSelector.whereAreYouShopping}
                    </p>
                    <CityRegionSelector
                      locale={locale}
                      onConfirm={onRegionConfirm}
                      onSkip={onRegionSkip}
                      currentSearchQuery={aiGeneratedQuery || searchInput}
                      uiContent={searchContent.regionSelector}
                    />
                  </AboveBarPromptPanel>
                ) : null}

                {/* "browsing the web" status while the product resolver searches — the
                    last row of the region so it sits right above the composer. */}
                {showBrowsingPill && (
                  <div className="flex justify-center px-3.5 pb-3 pt-3 sm:px-4">
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-zinc-100 px-3.5 py-1.5 text-xs lowercase text-zinc-500 dark:bg-white/10 dark:text-zinc-400">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-cornflowerblue/70" />
                      {browsingLabel}
                    </div>
                  </div>
                )}
                </>
                ) : null}
              </CardTopRegion>
            )}
            {/* Composer row: textarea + footer. A real box in composer layout so the
                phone keyboard policy can centre THIS (not the whole card) — the
                classic pill has no separate row, so it stays `contents`. */}
            <div ref={composerRowRef} className={isComposerLayout ? 'flex w-full flex-col' : 'contents'}>
            <div className={isComposerLayout ? 'relative w-full' : 'contents'}>
          <textarea
            id="search-textarea"
            ref={inputRef}
            data-search-bar
            data-ask-target={isComposerLayout ? undefined : ''}
            className={textareaClassName}
            disabled={composerDisabled}
            // Speaking, not typing: block edits without the disabled look.
            readOnly={isVoiceListening}
            style={{
              minHeight: TEXTAREA_HEIGHT,
              resize: 'none',
              // Inline (not a pr-* class — px-4/pr-11 would win or lose on
              // stylesheet order, not class order): clears the right-docked
              // wave while the live transcript streams.
              ...(isVoiceListening && liveTranscript ? { paddingRight: '5.5rem' } : {}),
            }}
            value={textareaValue}
            onChange={handleInput}
            onSelect={handleSelect}
            onFocus={async () => {
              setIsFocused(true);
              setFocusingOnTextArea(true);
              // Safety: clear a stuck picker flag on browsers that fire
              // neither `cancel` nor a usable window focus (pre-16.4 iOS).
              setIsImagePickerOpen(false);
              // Phones: morph into the composer capsule so "add an image" and the
              // price chip are reachable straight away.
              if (isPhoneLayout) setFocusComposerOpen(true);
              // Dismiss tutorial demo callout when user focuses on search bar
              onSearchFocus?.();
              // Re-focusing the search bar restores a collapsed panel — without
              // touching the composer text, so the conversation resumes as-is.
              if (isMinimized) {
                setIsMinimized(false);
              }
              if (searchInput.length > 0 || messageIsNotEmpty) {
                setIsSearching(true);
              } else {
                setIsDemo(true);
              }
              // No scrolling here: the visualViewport effect above lifts the
              // search block only once a real keyboard shows, and lowers it
              // when the keyboard leaves.
            }}
            onBlur={async () => {
              setIsFocused(false);
              setFocusingOnTextArea(false);

              // Don't collapse the search bar if we have an active search with messages
              if (messages.length > 0 || isLoading) {
                return;
              }
              
              await new Promise((resolve) => setTimeout(resolve, 500));
              setIsDemo(false);
              setDemoType?.(null);
              setShowNote(false);
            }}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={
              isVoiceListening || isSpeechProcessing
                ? ''
                : searchPropositions.length === 0 && messageIsNotEmpty && !isMinimized
                  ? isDiscussionTurn
                    ? (searchContent.discussionReplyPlaceholder ?? searchContent.answerPlaceholder)
                    : searchContent.answerPlaceholder
                  : isDemo
                    ? idlePlaceholder
                    : topMenu && isSearching && searchInput.length > 0
                      ? ''
                      : idlePlaceholder
            }
          />

            {/* Composer overlays — scoped to the textarea row so they center on it.
                Once live words stream into the field the wave yields the floor and
                docks right; processing feedback lives in the footer bar instead. */}
            {isComposerLayout && (
              <VoiceWaveAnimation
                isActive={isVoiceListening}
                audioStream={audioStream}
                align={liveTranscript ? 'right' : 'center'}
              />
            )}
            </div>

            {/* Composer footer — attach + filter chips on the left, mic + send on the
                right. While the mic is live (or finishing) every control steps aside
                for a single full-width stop/processing bar: one obvious tap target. */}
            {isComposerLayout && (
              <div className="flex animate-fade-in-from-below items-center justify-between gap-2 pb-2 pl-2 pr-2.5 pt-0.5">
                {showLoadingProducts && !isRestoredTurn ? (
                  <LoadingStatusRow locale={locale} isFinishing={!!result} onStop={handleStopSearch} />
                ) : (
                <>
                {!voiceComposerTakeover && (
                <div className="flex min-w-0 items-center gap-1.5">
                  {/* "+": add a photo, a link or a note (44px target). */}
                  {(onAddImage || onAddLink || onAddNote) && !(isDoneFinal && !algoliaMatchs) && (
                    <button
                      ref={attachButtonRef}
                      type="button"
                      onClick={(e) => toggleAttachMenu(e.detail === 0)}
                      onMouseDown={(e) => e.preventDefault()}
                      aria-label={searchContent.addAttachment}
                      title={searchContent.addAttachment}
                      aria-haspopup="menu"
                      aria-expanded={isAttachMenuOpen}
                      aria-controls={isAttachMenuOpen ? attachMenuId : undefined}
                      className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-zinc-600 transition-colors hover:bg-neutral-100 dark:text-zinc-300 dark:hover:bg-white/10 ${
                        isAttachMenuOpen ? 'bg-neutral-100 dark:bg-white/10' : ''
                      }`}
                      name="attach-button"
                    >
                      <PlusIcon className={`h-6 w-6 transition-transform duration-200 motion-reduce:transition-none ${isAttachMenuOpen ? 'rotate-45' : ''}`} />
                    </button>
                  )}
                  {/* The budget stays hidden until a search actually starts: a
                      control next to an empty bar reads as a paywall. */}
                  {!showRegionSelector && (activeRegionLabel || (messageIsNotEmpty && !hideBudgetChip)) && (
                    <SearchBarPills
                      regionLabel={activeRegionLabel}
                      regionPillLabel={searchContent.regionPillLabel}
                      onRegionClick={onReOpenRegionSelector}
                      showBudget={messageIsNotEmpty && !hideBudgetChip && !!onBudgetCommit}
                      budgetLabel={budget ? formatBudgetLabel(budget, locale) : null}
                      budgetMax={budget?.max ?? null}
                      budgetEmptyLabel={budgetUi.chipEmpty}
                      budgetAriaLabel={
                        budget
                          ? fillBudgetCopy(budgetUi.chipAria, { budget: formatBudgetLabel(budget, locale) })
                          : budgetUi.chipEmptyAria
                      }
                      budgetFieldLabel={budgetUi.fieldAria}
                      currency={budgetCurrencyAdornment(locale)}
                      onBudgetCommit={onBudgetCommit}
                      onBudgetEditingChange={setIsEditingBudget}
                    />
                  )}
                  <AttachmentChipsRail items={attachments} locale={locale} onRemove={onRemoveAttachment ? handleRemoveAttachment : undefined} />
                </div>
                )}
                <div className={voiceComposerTakeover ? 'flex w-full min-w-0 items-center' : 'flex flex-shrink-0 items-center gap-1.5'}>
                  {enterPressed || (isDoneFinal && !algoliaMatchs && !isRestoredTurn) ? (
                    <div className="mx-1.5 my-1.5 h-6 w-6 animate-spin rounded-full border-b-2 border-t-2 border-gray-400 dark:border-gray-300" />
                  ) : (
                    <>
                      {/* History (clock): re-homes Search history / Past chats now
                          that the classic bar's "?" menu is gone. */}
                      {!voiceComposerTakeover && (
                      <button
                        ref={historyButtonRef}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setIsAttachMenuOpen(false);
                          setShowHelpOptions((v) => !v);
                        }}
                        aria-label={searchContent.helpAssistance.historyMenuTitle ?? searchContent.helpAssistance.searchHistory}
                        title={searchContent.helpAssistance.historyMenuTitle ?? searchContent.helpAssistance.searchHistory}
                        className="flex h-11 w-11 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-neutral-100 dark:text-zinc-300 dark:hover:bg-white/10"
                        name="search-history-button"
                      >
                        <ClockIcon className="h-5 w-5" />
                      </button>
                      )}
                      {/* Kept mounted (display:none) during the takeover — this
                          component owns the MediaRecorder; unmounting it would
                          kill the recording mid-sentence. */}
                      <div
                        ref={speechToTextRef}
                        onMouseDown={(e) => e.preventDefault()}
                        className={
                          voiceComposerTakeover
                            ? 'hidden'
                            : 'flex h-11 w-11 items-center justify-center rounded-full transition-colors hover:bg-neutral-100 dark:hover:bg-white/10'
                        }
                      >
                        {renderVoiceSearchButton('lg')}
                      </div>
                      {voiceComposerTakeover && renderVoiceStopControl('bar')}
                      {!voiceComposerTakeover && (
                      <button
                        ref={buttonRef}
                        type="button"
                        aria-label={
                          !canSend
                            ? (searchContent.clearSearch ?? 'Clear search')
                            : searchContent.searchForProducts
                        }
                        className={`flex !h-11 !w-11 items-center justify-center rounded-full transition-colors disabled:pointer-events-none disabled:opacity-50 ${
                          !canSend
                            ? 'text-gray-500 hover:bg-neutral-100 dark:text-zinc-300 dark:hover:bg-white/10'
                            : 'bg-cornflowerblue text-white hover:bg-cornflowerblue/90'
                        }`}
                        disabled={canSend && isSubmitting}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          if (isStopEcho()) return;
                          if (!canSend) {
                            // If the region selector is open, close it only — don't cancel the search
                            if (showRegionSelector && onRegionFocusDismiss) {
                              onRegionFocusDismiss();
                            } else {
                              cancelSearch();
                            }
                          } else {
                            // If there's input, trigger search (prevent multiple submits)
                            if (!isSubmitting) {
                              pressSearch();
                            }
                          }
                        }}
                        name={!canSend ? 'cancel-search-button' : 'submit-search-button'}
                      >
                        {!canSend ? (
                          <MdClear className="h-6 w-6" />
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-5 w-5"
                          >
                            <path d="M12 19V5" />
                            <path d="m5 12 7-7 7 7" />
                          </svg>
                        )}
                      </button>
                      )}
                    </>
                  )}
                </div>
                </>
                )}
              </div>
            )}
            </div>
            {/* The foot of the discussion card: the shelf portals its "Might interest
                you" bar in here (live-results-shelf.tsx), so the picks are one tap
                away from the bottom of the same block. Phone hero only: everywhere
                else the shelf is already on screen under the card. Empty = no height. */}
            {showThreadPanel && isPhoneLayout && heroAnchored && <div ref={setPeekHost} data-shelf-peek-host />}
          </div>

          {/* Voice Wave Animation - shown when listening (classic single-row bar) */}
          {!isComposerLayout && (
            <VoiceWaveAnimation
              isActive={isVoiceListening}
              audioStream={audioStream}
              align={liveTranscript ? 'right' : 'center'}
            />
          )}

          {/* Classic single-row bar: the unmissable stop affordance floats right
              under the bar while the mic is live (the composer gets the footer
              bar instead). Same control, pill-shaped. */}
          {!isComposerLayout && isVoiceListening && (
            <div className="absolute left-0 right-0 top-full z-[110] mt-3 flex justify-center animate-fade-in-from-above">
              {renderVoiceStopControl('pill')}
            </div>
          )}

          {/* Speech Processing Loading Overlay (classic single-row bar) */}
          {!isComposerLayout && isSpeechProcessing && (
            <div
              className={`absolute inset-0 flex items-center pointer-events-none z-30 ${
                searchInput.length === 0 ? 'justify-center' : 'justify-end pr-11'
              }`}
            >
              {speechProcessingDots}
            </div>
          )}

          <div
            id="search-spinner"
            className={`h-6 w-6 animate-spin rounded-full ${
              (enterPressed || (!algoliaMatchs && isDoneFinal)) && !isComposerLayout
                ? 'absolute right-0 max-sm:right-2 border-b-2 border-t-2 border-gray-400 dark:border-gray-300 md:right-3'
                : 'hidden'
            }`}
          />
          
          {/* Regular button inside textarea - hidden in the composer capsule (its
              send/mic controls live in the footer row instead) */}
          {!isComposerLayout && (
            <button
              ref={buttonRef}
              className={`absolute items-center justify-center bg-white py-1.5 disabled:pointer-events-none v right-0.5 disabled:opacity-50 dark:bg-dark/80 ${
                showingSuggestions ? 'rounded-md rounded-b-none rounded-tl-none' : 'rounded-full rounded-l-none pl-2.5'
              } ${
                isSearching
                  ? `!ml-0 rounded-md hover:bg-neutral-100 dark:hover:bg-cornflowerblue/5 hover:border-l hover:rounded-l-md dark:hover:border-transparent-dark`
                  : ''
              } ${
                enterPressed || (isDoneFinal && !algoliaMatchs) || isVoiceListening ? 'hidden' : ''
              } ${isDoneFinal && algoliaMatchs ? '!border-l-0 hover:!bg-transparent dark:!border-transparent-dark opacity-50' : ''} ${!messageIsNotEmpty || isSwapped ? 'px-[0.8rem]' : 'px-2'}`}
              disabled={isDoneFinal && !algoliaMatchs}
              style={{  resize: 'none' }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (isStopEcho()) return;
                if (messageIsNotEmpty && !isSwapped) {
                  // When showing clear icon (MdClear) and not swapped, clear everything
                  cancelSearch();
                } else if (searchInput.length === 0) {
                  // Empty bar: the right-side button is the "+" (photo, link, note).
                  if (canAttach) toggleAttachMenu(false);
                } else {
                  // Normal search behavior when there's input but no messages, or when swapped
                  pressSearch();
                }
              }}
              aria-label={
                messageIsNotEmpty && !isSwapped
                  ? (searchContent.clearSearch ?? 'Clear search')
                  : searchInput.length === 0 && canAttach
                    ? searchContent.addAttachment
                    : searchContent.searchForProducts
              }
              {...(searchInput.length === 0 && canAttach && (!messageIsNotEmpty || isSwapped)
                ? {
                    'aria-haspopup': 'menu' as const,
                    'aria-expanded': isAttachMenuOpen,
                    'aria-controls': isAttachMenuOpen ? attachMenuId : undefined,
                  }
                : {})}
              name="search-button"
            >
              {!messageIsNotEmpty || isSwapped ? (
                searchInput.length === 0 && canAttach ? (
                  <PlusIcon className={`h-5 w-5 transition-transform duration-200 motion-reduce:transition-none ${isAttachMenuOpen ? 'rotate-45' : ''}`} />
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4 stroke-current dark:stroke-white"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.3-4.3"></path>
                  </svg>
                )
              ) : (
                <MdClear className="h-6 w-6" />
              )}
            </button>
          )}
          
        </div>

        {/* Search rejected - minimal inline hint below */}
        {/* Suppressed while the error banner is up: a degraded search sets both
            (the banner only renders once a conversation exists, this line always
            does), and showing the same sentence twice reads as a glitch. */}
        {searchRejectedMessage && !productSearchParseError && !showRegionSelector && (
          <p className="mt-2.5 text-center text-xs text-zinc-400 dark:text-zinc-500 tracking-wide select-none">
            {searchRejectedMessage}
          </p>
        )}

        {/* Tap-to-fill example chips when focused-empty (or in tutorial mode).
            Hidden the instant the bar blurs — otherwise they jump into the
            collapsing footer slot and steal the next tap (preventDefault).
            Hidden while dictating — the mic takeover keeps a single message on
            screen: stop talking to continue. */}
        {!compactMode && isFocused && (isDemo || tutorialMode) && !topMenu && !isSearching && !isVoiceListening && messages.length === 0 && searchInput.length === 0 && (
          <SuggestionChips locale={locale} tutorialMode={tutorialMode} onPick={handleSuggestionPick} />
        )}
        
       

        {/* DISABLED: PreSearchSuggestionList to avoid token consumption */}
        {/* {showingSuggestions && !showRegionSelector && (
          <div
            className={`absolute ${topMenu ? 'dark:bg-dark/80' : 'dark:bg-dark/50'} ${
              header ? `${headerWidth} ${headerLeftAlign} max-md:ml-4` : ''
            } top-full z-30 -mt-[1px] w-full rounded-md rounded-t-none`}
            ref={suggestionListRef}
          >
            <ul className={`w-full dark:border dark:border-transparent-dark pb-2 shadow-search-suggestion dark:shadow-search-suggestion-dark`}>
              <PreSearchSuggestionList
                highlight={highlightedIndex}
                locale={locale}
                setInput={setInput}
                setSearchInput={setSearchInput}
                suggestions={parseSuggestionsMessage(searchPropositions[searchPropositions.length - 1])}
              />
            </ul>
          </div>
        )} */}

        {/* Help Panel — wrapped in div for click-outside ref (dynamic component cannot receive ref) */}
        <div ref={attachMenuRef}>
          <AttachMenu
            locale={locale}
            isVisible={isAttachMenuOpen && !isVoiceListening}
            menuId={attachMenuId}
            header={header}
            headerWidth={headerWidth}
            headerRightAlign={headerRightAlign}
            focusOnOpen={attachMenuFromKeyboard}
            canAddPhoto={Boolean(onAddImage)}
            onClose={closeAttachMenu}
            onPickPhoto={() => openImagePicker(attachMenuHadFocusRef.current)}
            onAddLink={(raw) => onAddLink?.(raw) ?? 'invalid'}
            onAddNote={(text) => onAddNote?.(text) ?? 'invalid'}
            onOpenHistory={() => setShowHelpOptions(true)}
          />
        </div>
        <div ref={helpPanelRef}>
          <SearchHelpPanel
            locale={locale}
            // With the "?" gone, the panel only opens from the explicit clock
            // button — and Past chats matters most mid-conversation, so it must
            // not be suppressed while a search is active.
            isVisible={showHelpOptions}
            historyOnly
            header={header}
            topMenu={topMenu}
            headerWidth={headerWidth}
            headerLeftAlign={headerLeftAlign}
            onClose={() => setShowHelpOptions(false)}
            onCompareProductsHelp={handleCompareProductsHelp}
            onContactHelp={handleContactHelp}
            onSearchHistorySelect={handleSearchHistorySelect}
            onResumeChat={(args) => {
              setShowHelpOptions(false);
              onResumeChat?.(args);
            }}
            availableProducts={
              productTitles?.map((title, index) => ({
                id: `product-${index}`,
                name: title,
              })) || []
            }
            onCompareProducts={handleCompareProducts}
          />
        </div>

        {/* Entered a product search and passed the search suggestions step, OR showing region selector, OR loading after region skip */}
        {/* ═══════════════════════════════════════════════════════════════════════════════════════════════════════════ */}
        {/* Q&A SECTION CONTAINER - This is what we refer to as the "Q&A section" or "messages container"              */}
        {/* Contains:                                                                                                    */}
        {/* 1. Region selector (when needed) - styled to match Q&A flow                                                 */}
        {/* 2. Messages component (chat/messages.tsx) - the main Q&A interface with questions and answer buttons        */}
        {/* ═══════════════════════════════════════════════════════════════════════════════════════════════════════════ */}
        {containerVisible && (
          <div
            // In the top bar this is the search dropdown, a scroller: globals.css keeps the
            // bar's reveal-box shift off its content.
            data-bar-scroller={header || undefined}
            className={`messages-container mx-auto ${
              header
                ? `${headerWidth} ${headerLeftAlign} absolute mt-4 rounded-2xl border border-input bg-canvas dark:bg-dark dark:border-transparent-dark backdrop-blur-sm shadow-search-bar dark:shadow-search-bar-dark pt-1 max-h-[calc(100vh-8rem)] overflow-y-auto overscroll-contain px-4 pb-4 `
                : 'min-h-fit'
            } ${
              header
                ? isDoneFinal && algoliaMatchs
                  ? 'pt-1 '
                  : isDiscussionTurn
                    ? 'pt-2'
                    : 'pt-10 max-sm:pt-7 max-xs:pt-5 max-xxs:pt-4'
                : useInPanelConversation
                  ? 'pt-6 max-sm:pt-4'
                  : 'pt-0'
            } w-full`}
            ref={messagesRef}
          >
            {/* Minimize/Expand Button - Only shown in header mode */}
            {header && (
              <button
                onClick={handleMinimizeToggle}
                className="absolute top-3 left-3 z-50 p-2 rounded-lg bg-gray-100/80 dark:bg-gray-800/50 hover:bg-gray-200 dark:hover:bg-gray-700/50 transition-all duration-200 shadow-sm backdrop-blur-sm"
                title={searchContent.minimizeSearchMenu}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-gray-600 dark:text-gray-300"
                >
                  <path d="M5 12h14" />
                </svg>
              </button>
            )}
            
            {/* Below-the-bar content. The topMenu desktop dropdown (and the non-product
                intents in every layout) keep the classic in-panel flow (overlay price
                picker, region map, conversation via Messages) and gain the live shelf at
                the bottom. The regular product-search layout moved the whole conversation
                ABOVE the bar, so down here only the live results shelf and the
                page-creation theater remain. */}
            {useInPanelConversation ? (
              <div className="relative">
                {/* Region Selector - Show inline when needed (styled to match Q&A section) */}
                {showRegionSelector && onRegionConfirm && onRegionSkip && (
                  <div className="mb-4">
                    <CityRegionSelector
                      locale={locale}
                      onConfirm={onRegionConfirm}
                      onSkip={onRegionSkip}
                      currentSearchQuery={aiGeneratedQuery || searchInput}
                      uiContent={searchContent.regionSelector}
                    />
                  </div>
                )}

                {/* Q&A SECTION - Main conversational interface (see chat/messages.tsx) */}
                {!showRegionSelector && (
                  <Messages
                    isLoading={isLoading}
                    locale={locale}
                    messages={messages}
                    setInput={setInput}
                    setSearchInput={setSearchInput}
                    algoliaMatchs={algoliaMatchs}
                    searchIndication={!isDoneFinal}
                    header={header}
                    isDoneFinal={isDoneFinal}
                    isProductBranchLoading={isProductBranchLoading}
                    pauseScrollOnMutation={showLoadingProducts}
                    currentSearchIntent={currentSearchIntent}
                    aiGeneratedQuery={aiGeneratedQuery}
                    isSwapped={isSwapped}
                    setIsSwapped={setIsSwapped}
                    currentQuestionPrompt={currentQuestionPrompt}
                    currentPreamble={currentPreamble}
                    latestAssistantId={latestAssistantId}
                    isFirstTurn={isFirstAssistantTurn}
                    questionTypewriterDone={questionTypewriterDone}
                    onQuestionTypewriterComplete={handleQuestionTypewriterComplete}
                    restoredTurnIds={restoredTurnIds}
                    instantStandBy={isStandByTurn}
                    topMenu={topMenu}
                    turnMode={latestTurnMode}
                    onSwitchToProductSearch={onSwitchToProductSearch}
                    onMinimize={handleMinimizeToggle}
                    productSearchParseError={productSearchParseError}
                    onProductSearchRetry={onProductSearchRetry}
                    failureMessage={searchRejectedMessage}
                    retryNotBefore={retryNotBefore}
                    focusVisuals={focusVisuals}
                    onShelfNavigate={onShelfNavigate}
                  >
                    {showCreationTheater && !hasShelfHits ? (
                      <LoadingExtras locale={locale} topMenu={topMenu} />
                    ) : isDiscussionTurn ? null : !isLoading ? (
                      (() => {
                        const lastAssistantMsg = [...messages].reverse().find((m) => m.role === 'assistant' || m.role === 'system');
                        const { suggestedUserAnswers: choices } = lastAssistantMsg
                          ? parseAssistantMessage({ message: lastAssistantMsg })
                          : { suggestedUserAnswers: [] };
                        const capitalizedCount = choices.filter(
                          (c) => c.length > 0 && c[0] === c[0].toUpperCase() && c[0] !== c[0].toLowerCase()
                        ).length;
                        const useUpperCase = choices.length > 0 && capitalizedCount > choices.length / 2;
                        const label = useUpperCase
                          ? searchContent.customSearch.charAt(0).toUpperCase() + searchContent.customSearch.slice(1)
                          : searchContent.customSearch.charAt(0).toLowerCase() + searchContent.customSearch.slice(1);
                        return (
                          <ButtonSuggestion
                            onMouseDown={(e) => e.preventDefault()}
                            handleSuggestionClick={handleFocusAndShowNote}
                            choice={searchContent.customSearch}
                          >
                            {label}
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="h-3.5 w-3.5 shrink-0 opacity-70"
                            >
                              <circle cx="11" cy="11" r="8"></circle>
                              <path d="m21 21-4.3-4.3"></path>
                            </svg>
                          </ButtonSuggestion>
                        );
                      })()
                    ) : null}
                  </Messages>
                )}

                {/* Live results shelf — products of the matched pages, inside the dropdown */}
                {!showRegionSelector && shelfVisible && (
                  <LiveResultsShelf
                    hits={shelfHits}
                    locale={locale}
                    searchQuery={aiGeneratedQuery || input || searchInput}
                    isLoading={isLoading}
                    isDoneFinal={isDoneFinal}
                    topMenu
                    kitMode={kitShelfMode}
                    kitElements={confirmedKitElements}
                    seedProduct={kitTraySeed}
                    onCreateNewPage={onCreatePageFromShelf}
                    onStartNewSearch={cancelSearch}
                    onNavigate={onShelfNavigate}
                    budgetCeiling={budget?.max ?? null}
                    budgetQuantity={budget?.quantity ?? null}
                    subregionScope={subregionScope}
                    discussion={isDiscussionTurn}
                  />
                )}
                {showShelfEmptied && <ShelfEmptiedNote locale={locale} isLoading={isLoading} topMenu />}
              </div>
            ) : (
              <div className="relative">
                {/* Live results shelf — always-visible product propositions for the
                    current conversation (and the assembling kit tray at kit scope);
                    refreshes every AI turn. */}
                {shelfVisible && (
                  <LiveResultsShelf
                    hits={shelfHits}
                    locale={locale}
                    searchQuery={aiGeneratedQuery || input || searchInput}
                    isLoading={isLoading}
                    isDoneFinal={isDoneFinal}
                    kitMode={kitShelfMode}
                    kitElements={confirmedKitElements}
                    seedProduct={kitTraySeed}
                    onCreateNewPage={onCreatePageFromShelf}
                    onStartNewSearch={cancelSearch}
                    onNavigate={onShelfNavigate}
                    budgetCeiling={budget?.max ?? null}
                    budgetQuantity={budget?.quantity ?? null}
                    subregionScope={subregionScope}
                    discussion={isDiscussionTurn}
                    peekHost={peekHost}
                    onPeekOpen={scrollToShelf}
                  />
                )}

                {/* The picks this conversation showed no longer fit its answers */}
                {showShelfEmptied && <ShelfEmptiedNote locale={locale} isLoading={isLoading} />}

                {/* Page-creation theater — only when there are no matched pages to show */}
                {showCreationTheater && !hasShelfHits && (
                  <LoadingExtras locale={locale} topMenu={topMenu} />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};
